import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';
import { getPreviewUrl, downloadDocument } from '../lib/documentPreview';
import usePasteUpload from '../hooks/usePasteUpload';
import { turkishIncludes } from '../lib/turkishSearch';
import { COUNTRIES, NATIONALITIES } from '../lib/countries';
import PostNLAssignedCard from '../components/PostNLAssignedCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import { ImportantDocumentsCard } from '../components/ImportantDocumentsCard';
import { UserVehicleHistory } from '../components/UserVehicleHistory';
import { UserAssignmentHistory } from '../components/UserAssignmentHistory';
import { validateRRN, getRRNErrorMessage, getRRNWarningMessage, stripRRN, formatRRN, validateNationalId, formatNationalId, getPlaceholder, getMaxDigits, stripDigits } from '../utils/rijksregisternummer';
import { 
  User, 
  FileText, 
  Calendar, 
  Euro, 
  Save, 
  Loader2, 
  Upload,
  Download,
  Trash2,
  Eye,
  Plus,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Building2,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Users,
  Briefcase,
  Clock,
  FileCheck,
  X,
  Search,
  Camera,
  IdCard,
  Car,
  FileCheck2,
  Wallet,
  Zap,
  Tag,
  Info,
  Package,
  BarChart3,
  Filter,
  TrendingUp,
  RefreshCw,
  LogIn,
  ArrowLeftRight,
  ScrollText,
  Pencil,
  Check,
  Sparkles,
  MessageCircle
} from 'lucide-react';
import { RehireDialog } from '../components/employee/RehireDialog';
import ImageCropper from '../components/ImageCropper';
import DocumentImageEditor from '../components/DocumentImageEditor';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

/**
 * Show a success toast with a real thumbnail for uploaded files.
 *
 * @param {File[]} files - Uploaded files (thumbnail sourced from first one)
 * @param {string} label - e.g., "Ehliyet", "Belge"
 * @param {string} [sub] - Optional secondary line (e.g., "Ön Yüz")
 * @param {Object} [undo] - Optional undo config
 * @param {string[]} undo.ids - Uploaded document IDs to delete on undo
 * @param {() => void} undo.onComplete - Called after successful undo (e.g., refresh list)
 */
function showUploadThumbnailToast(files, label, sub, undo) {
  if (!files?.length) return;
  const first = files[0];
  const isImg = first.type?.startsWith('image/');
  const thumbUrl = isImg ? URL.createObjectURL(first) : null;
  const count = files.length;
  const duration = undo?.ids?.length ? 5500 : 3500;
  toast.custom(
    (toastId) => (
      <div className="flex items-center gap-3 w-[340px] p-3 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-emerald-200 dark:border-emerald-800">
        <div className="relative shrink-0 w-12 h-12 rounded overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
          {thumbUrl ? (
            <img src={thumbUrl} alt={first.name} className="w-full h-full object-cover" />
          ) : (
            <FileText className="h-6 w-6 text-red-500" />
          )}
          {count > 1 && (
            <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-white dark:border-slate-800">
              {count}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
            <Check className="h-3.5 w-3.5 stroke-[3]" />
            <span className="text-xs font-semibold uppercase tracking-wide">
              {count > 1 ? `${count} Dosya Yüklendi` : 'Yüklendi'}
            </span>
          </div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate mt-0.5">
            {label}{sub ? <span className="text-slate-400 font-normal"> ({sub})</span> : null}
          </p>
          <p className="text-[11px] text-slate-500 truncate">
            {first.name}{count > 1 ? ` +${count - 1}` : ''}
          </p>
        </div>
        {undo?.ids?.length > 0 && (
          <button
            onClick={async () => {
              toast.dismiss(toastId);
              try {
                await Promise.all(undo.ids.map(id => api.delete(`/documents/${id}`)));
                toast.success('Yükleme geri alındı');
                undo.onComplete?.();
              } catch {
                toast.error('Geri alma başarısız');
              }
            }}
            className="shrink-0 text-xs font-semibold text-slate-600 hover:text-red-600 dark:text-slate-300 dark:hover:text-red-400 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 hover:border-red-300 transition-colors"
            data-testid="upload-undo-btn"
          >
            Geri Al
          </button>
        )}
      </div>
    ),
    { duration },
  );
  if (thumbUrl) setTimeout(() => URL.revokeObjectURL(thumbUrl), duration + 500);
}

// Chart colors
const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#f97316'];

// Important document types that should be highlighted
const IMPORTANT_DOC_TYPES = ['identiteitskaart', 'paspoort', 'rijbewijs', 'identity_card', 'passport', 'driver_license'];

// Document type keys for i18n mapping
const DOCUMENT_TYPE_KEYS = {
  identiteitskaart: 'idCard',
  paspoort: 'passport',
  rijbewijs: 'driverLicense',
  verblijfsvergunning: 'residencePermit',
  arbeidsovereenkomst: 'workContract',
  diploma: 'diploma',
  medisch_attest: 'medicalCert',
  bankgegevens: 'bankDetails',
  belastingfiche: 'taxForm',
  andere: 'other'
};

// Finance type keys for i18n mapping
const FINANCE_TYPE_KEYS = {
  avans: { key: 'advance', color: 'bg-yellow-500' },
  pot_plus: { key: 'potPlus', color: 'bg-emerald-500' },
  pot_minus: { key: 'potMinus', color: 'bg-rose-500' },
  laadvergoeding: { key: 'laadvergoeding', color: 'bg-cyan-500' },
  leasing: { key: 'leasing', color: 'bg-purple-500' },
  diger: { key: 'other', color: 'bg-slate-50 dark:bg-slate-8000' }
};

// Marital status keys for i18n mapping
const MARITAL_STATUS_KEYS = {
  ongehuwd: 'single',
  gehuwd: 'married',
  gescheiden: 'divorced',
  weduwe_weduwnaar: 'widowed',
  wettelijk_samenwonend: 'cohabiting'
};

export default function AdminEmployeeProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { user: currentUser } = useAuthStore();
  const fileInputRef = useRef(null);
  
  // Financial visibility check
  const canViewFinancial = currentUser?.role === 'SUPER_ADMIN' || currentUser?.permissions?.can_view_financial === true;
  // Şub 09, 2026 — tab-level permissions: SUPER_ADMIN her zaman görür;
  // diğerleri için allowed_pages boşsa "kısıtlama yok" demektir (tüm tablar görünür).
  const _allowedPages = currentUser?.permissions?.allowed_pages || [];
  const _hasPagePerm = (key) => currentUser?.role === 'SUPER_ADMIN' || _allowedPages.length === 0 || _allowedPages.includes(key);
  const canViewPackagesTab = _hasPagePerm('employees-tab-packages');
  const canViewLoonbriefsTab = _hasPagePerm('employees-tab-loonbriefs');
  const canViewNotesTab = _hasPagePerm('employees-tab-notes');
  // Finansal tab: eski toggle (can_view_financial) VE yeni page key birlikte gerekli
  const canViewFinanceTab = canViewFinancial && _hasPagePerm('employees-tab-finance');
  
  // Şub 09, 2026 — URL ?tab=X destekle (Personel Özet'ten gelirken)
  const [searchParams] = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return [sp];
    } catch { return [new URLSearchParams()]; }
  })();
  const initialTab = ['personal', 'documents', 'packages', 'loonbriefs', 'history', 'finance', 'notes', 'firm-history'].includes(searchParams.get('tab'))
    ? searchParams.get('tab') : 'personal';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // User and Profile data
  const [targetUser, setTargetUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileExists, setProfileExists] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [firms, setFirms] = useState([]);

  // Fetch firm list once (needed for PostNL assignment card)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/firms/');
        if (!cancelled) setFirms(res.data?.data || []);
      } catch { /* silent — non-critical */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Firm transfer history (loaded when "firm-history" tab opens)
  const [firmHistory, setFirmHistory] = useState(null); // {current_firm, history[], created_at}
  const [firmHistoryLoading, setFirmHistoryLoading] = useState(false);
  
  // Documents
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploadDocDialog, setUploadDocDialog] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDocFiles, setSelectedDocFiles] = useState([]); // Changed to array for multiple files
  const [customDocName, setCustomDocName] = useState('');
  const [docCategory, setDocCategory] = useState('PERSONAL');
  const [isDragging, setIsDragging] = useState(false); // For drag-drop state
  const [isTabDragging, setIsTabDragging] = useState(false); // For tab-level drag-drop
  const docFileInputRef = useRef(null);

  // Safety net: if a drag ends anywhere (even outside our drop zones) or the
  // browser fires a generic drop on window, clear lingering highlight states.
  // Without this, quickly flicking the cursor in/out can leave `isTabDragging`
  // stuck `true` → blue overlay never disappears.
  useEffect(() => {
    const clearDragStates = () => {
      setIsTabDragging(false);
      setIsDragging(false);
    };
    window.addEventListener('drop', clearDragStates);
    window.addEventListener('dragend', clearDragStates);
    return () => {
      window.removeEventListener('drop', clearDragStates);
      window.removeEventListener('dragend', clearDragStates);
    };
  }, []);

  // Document table pagination & sort (newest first)
  const [docPage, setDocPage] = useState(1);
  const [docPageSize, setDocPageSize] = useState(15);
  const [docSearch, setDocSearch] = useState('');
  const [docCategoryFilter, setDocCategoryFilter] = useState('ALL'); // ALL | PERSONAL | COMPANY | IMPORTANT
  
  // Document delete & edit
  const [deleteDocDialog, setDeleteDocDialog] = useState({ open: false, doc: null });
  const [deletingDoc, setDeletingDoc] = useState(false);
  const [editDocDialog, setEditDocDialog] = useState({ open: false, doc: null });
  const [editingDoc, setEditingDoc] = useState(false);
  const [editDocCategory, setEditDocCategory] = useState('PERSONAL');
  const [editDocFileName, setEditDocFileName] = useState('');
  
  // Profile Photo
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoCropperOpen, setPhotoCropperOpen] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Important document upload
  const [uploadingImportantDoc, setUploadingImportantDoc] = useState(false);
  
  // Finance
  const [finance, setFinance] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeDialog, setFinanceDialog] = useState(false);
  const [financeForm, setFinanceForm] = useState({
    entry_type: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });
  const [savingFinance, setSavingFinance] = useState(false);
  const [potDetailDialog, setPotDetailDialog] = useState(false);
  const [financeFilter, setFinanceFilter] = useState('all');
  const [financeMonth, setFinanceMonth] = useState(new Date().getMonth() + 1);
  const [financeYear, setFinanceYear] = useState(new Date().getFullYear());
  const [financeDateFrom, setFinanceDateFrom] = useState('');
  const [financeDateTo, setFinanceDateTo] = useState('');
  
  // Packages (Paketler)
  const [packages, setPackages] = useState(null);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesRegions, setPackagesRegions] = useState([]);
  const [packagesDateRange, setPackagesDateRange] = useState(null);
  const [packageStartDate, setPackageStartDate] = useState('');
  const [packageEndDate, setPackageEndDate] = useState('');
  const [packageRegionFilter, setPackageRegionFilter] = useState('');
  const [selectedPackagePreset, setSelectedPackagePreset] = useState('');
  
  // Employee Notes
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState(null); // For delete confirmation dialog
  
  // Filtered finance data
  const filteredFinance = useMemo(() => {
    if (!finance) return null;
    
    let filteredEntries = [...finance.entries];
    
    if (financeFilter !== 'all') {
      filteredEntries = finance.entries.filter(entry => {
        const entryDate = new Date(entry.date);
        const entryMonth = entryDate.getMonth() + 1;
        const entryYear = entryDate.getFullYear();
        
        if (financeFilter === 'month') {
          return entryMonth === financeMonth && entryYear === financeYear;
        } else if (financeFilter === 'year') {
          return entryYear === financeYear;
        } else if (financeFilter === 'lastMonth') {
          const lastMonth = new Date();
          lastMonth.setMonth(lastMonth.getMonth() - 1);
          return entryMonth === (lastMonth.getMonth() + 1) && entryYear === lastMonth.getFullYear();
        } else if (financeFilter === 'range' && financeDateFrom && financeDateTo) {
          const from = new Date(financeDateFrom);
          const to = new Date(financeDateTo);
          return entryDate >= from && entryDate <= to;
        }
        return true;
      });
    }
    
    // Recalculate summary
    let total_avans = 0, total_debt = 0, total_paid = 0, total_bonus = 0;
    let pot_plus = 0, pot_minus = 0, laadvergoeding = 0, total_deductions = 0;
    
    filteredEntries.forEach(entry => {
      const amount = entry.amount;
      const type = entry.entry_type;
      
      if (type === 'avans') { total_avans += amount; total_debt += amount; }
      else if (type === 'borc') { total_debt += amount; }
      else if (type === 'odeme') { total_paid += amount; }
      else if (type === 'kesinti') { total_deductions += amount; }
      else if (type === 'prim') { total_bonus += amount; }
      else if (type === 'pot_plus') { pot_plus += amount; }
      else if (type === 'pot_minus') { pot_minus += amount; }
      else if (type === 'laadvergoeding') { laadvergoeding += amount; }
    });
    
    return {
      entries: filteredEntries,
      summary: {
        total_avans, total_debt, total_paid, total_bonus, total_deductions,
        pot_plus, pot_minus, pot_balance: pot_plus - pot_minus, laadvergoeding,
        net_balance: total_paid - total_debt + total_bonus - total_deductions + (pot_plus - pot_minus) + laadvergoeding
      }
    };
  }, [finance, financeFilter, financeMonth, financeYear, financeDateFrom, financeDateTo]);
  
  // History
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Loonbriefs (Payslips)
  const [loonbriefs, setLoonbriefs] = useState([]);
  const [loonbriefsLoading, setLoonbriefsLoading] = useState(false);
  const [lbFilterYear, setLbFilterYear] = useState('all');
  const [lbFilterMonth, setLbFilterMonth] = useState('all');
  
  // Document preview (unified - handles both images and PDFs via DocumentPreviewModal)
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // OCR Result Modal
  const [ocrModalOpen, setOcrModalOpen] = useState(false);
  const [ocrModalData, setOcrModalData] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    naam: '',
    voornaam: '',
    geboortedatum: '',
    geboorteplaats: '',
    nationaliteit: '',
    rijksregisternummer: '',
    burgerlijke_staat: '',
    aliases: [],  // Lakaplar / İkincil isimler for payroll matching
    hide_postnl_packages: false,  // Mart 01, 2026 — Yanlış ad eşleşmesini engellemek için
    straat: '',
    huisnummer: '',
    postcode: '',
    woonplaats: '',
    land: 'België',
    gsm: '',
    email: '',
    bankrekening_iban: '',
    bloedgroep: '',
    id_expiry_date: '',
    license_expiry_date: '',
    driver_attest_expiry_date: '',
    fiscale_gezinssituatie: {
      kinderen_ten_laste: false,
      aantal_kinderen: null,
      echtgenoot_ten_laste: false,
      andere_personen_ten_laste: false,
      aantal_andere_personen: null
    }
  });

  // Delete confirmations
  const [deleteFinanceEntry, setDeleteFinanceEntry] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchUserProfile();
      fetchDocuments(); // Fetch documents on initial load for profile photo
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (activeTab === 'documents' && documents.length === 0 && userId) {
      fetchDocuments();
    } else if (activeTab === 'finance' && !finance && userId) {
      fetchFinance();
    } else if (activeTab === 'history' && !history && userId) {
      fetchHistory();
    } else if (activeTab === 'loonbriefs' && loonbriefs.length === 0 && userId) {
      fetchLoonbriefs();
    } else if (activeTab === 'packages' && !packages && userId) {
      fetchPackagesRegions();
      fetchPackages();
    } else if (activeTab === 'notes' && notes.length === 0 && userId) {
      fetchNotes();
    } else if (activeTab === 'firm-history' && !firmHistory && userId) {
      fetchFirmHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, userId]);

  const fetchFirmHistory = async () => {
    try {
      setFirmHistoryLoading(true);
      const res = await api.get(`/employee-profile/user/${userId}/firm-history`);
      if (res.data?.success) setFirmHistory(res.data.data);
    } catch (err) {
      console.error('Firm history fetch error', err);
    } finally {
      setFirmHistoryLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/employee-profile/user/${userId}`);
      if (response.data.success) {
        const data = response.data.data;
        setTargetUser(data.user);
        setProfileExists(data.exists);
        setProfileCompletion(data.profile_completion_percentage);
        
        if (data.profile) {
          setProfile(data.profile);
          setFormData(prev => ({
            ...prev,
            ...data.profile,
            aliases: data.profile.aliases || [],
            hide_postnl_packages: data.profile.hide_postnl_packages || false,
            fiscale_gezinssituatie: data.profile.fiscale_gezinssituatie || prev.fiscale_gezinssituatie
          }));
        } else if (data.user) {
          // Pre-fill from user data
          // Parse full_name into first and last name if not available
          const firstName = data.user.first_name || (data.user.full_name || '').split(' ')[0] || '';
          const lastName = data.user.last_name || (data.user.full_name || '').split(' ').slice(1).join(' ') || '';
          
          setFormData(prev => ({
            ...prev,
            voornaam: firstName,
            naam: lastName,
            email: data.user.email || '',
            gsm: data.user.gsm || data.user.phone || ''
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Profiel laden mislukt');
      navigate('/dashboard/employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setDocumentsLoading(true);
      // Fetch documents for this specific user via backend filtering
      const response = await api.get(`/documents?for_user_id=${userId}`);
      const allDocs = Array.isArray(response.data) ? response.data : [];
      setDocuments(allDocs);
      
      // Find profile photo from documents and load it with auth
      const photoDoc = allDocs.find(d => d.document_type === 'profile_photo');
      if (photoDoc) {
        try {
          // Şub 27, 2026 — Direct CDN URL ile anında yükleme
          const { url } = await getPreviewUrl(api, photoDoc.id, true);
          if (url) setProfilePhoto(url);
        } catch (photoError) {
          console.error('Error loading profile photo:', photoError);
        }
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const fetchFinance = async () => {
    try {
      setFinanceLoading(true);
      const response = await api.get(`/employee-profile/finance/${userId}`);
      if (response.data.success) {
        setFinance(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching finance:', error);
    } finally {
      setFinanceLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await api.get(`/employee-profile/history/${userId}`);
      if (response.data.success) {
        setHistory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchLoonbriefs = async () => {
    try {
      setLoonbriefsLoading(true);
      const response = await api.get(`/loonbrief/user/${userId}`);
      if (response.data.success) {
        setLoonbriefs(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching loonbriefs:', error);
    } finally {
      setLoonbriefsLoading(false);
    }
  };

  const handleDownloadLoonbrief = async (docId, fileName) => {
    const ok = await downloadDocument(api, { id: docId, original_name: fileName || 'loonbrief.pdf' });
    if (!ok) toast.error('Dosya indirilirken hata oluştu');
  };

  const handlePreviewLoonbrief = (doc) => {
    // Use unified DocumentPreviewModal for loonbriefs (PDFs)
    setPreviewDoc({
      id: doc.id,
      file_name: doc.file_name,
      file_type: 'application/pdf'
    });
  };

  const fetchPackagesRegions = async () => {
    try {
      const response = await api.get(`/loonbrief/user-packages-regions/${userId}`);
      if (response.data.success) {
        setPackagesRegions(response.data.data.regions || []);
        setPackagesDateRange(response.data.data.date_range);
      }
    } catch (error) {
      console.error('Error fetching packages regions:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      setPackagesLoading(true);
      const params = new URLSearchParams();
      if (packageStartDate) params.append('start_date', packageStartDate);
      if (packageEndDate) params.append('end_date', packageEndDate);
      if (packageRegionFilter) params.append('region', packageRegionFilter);
      
      const response = await api.get(`/loonbrief/user-packages/${userId}?${params.toString()}`);
      if (response.data.success) {
        setPackages(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setPackagesLoading(false);
    }
  };

  const handleImpersonate = async () => {
    try {
      const response = await api.post(`/admin/users/${userId}/impersonate`);
      if (response.data.success) {
        const { token, user_email, user_name } = response.data.data;
        
        // Get auth store functions
        const authStore = useAuthStore.getState();
        
        // Set new token in Zustand store (this will auto-persist to localStorage)
        authStore.setToken(token);
        
        // Fetch user info with the new token
        await authStore.fetchUser();
        
        toast.success(`${user_name || user_email} olarak giriş yapıldı`);
        
        // Navigate to dashboard (no page reload needed)
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error impersonating user:', error);
      toast.error(error.response?.data?.detail || 'Kullanıcı hesabına giriş yapılamadı');
    }
  };

  // Fetch employee notes
  const fetchNotes = async () => {
    try {
      setNotesLoading(true);
      const response = await api.get(`/loonbrief/employee-notes/${userId}`);
      if (response.data.success) {
        setNotes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setNotesLoading(false);
    }
  };

  // Add new note
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      setAddingNote(true);
      const response = await api.post('/loonbrief/employee-notes', {
        user_id: userId,
        note: newNote.trim()
      });
      if (response.data.success) {
        toast.success('Not eklendi');
        setNewNote('');
        fetchNotes();
      }
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Not eklenemedi');
    } finally {
      setAddingNote(false);
    }
  };

  // Delete note
  // Open delete confirmation dialog
  const handleDeleteNoteClick = (noteId) => {
    setDeleteNoteId(noteId);
  };

  // Confirm delete note
  const handleConfirmDeleteNote = async () => {
    if (!deleteNoteId) return;
    
    try {
      const response = await api.delete(`/loonbrief/employee-notes/${deleteNoteId}`);
      if (response.data.success) {
        toast.success('Not silindi');
        fetchNotes();
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Not silinemedi');
    } finally {
      setDeleteNoteId(null);
    }
  };

  // Handle OCR result from ImportantDocumentsCard
  const handleOcrComplete = (ocrData) => {
    if (!ocrData) return;
    // Always open the confirmation modal, even when nothing was detected,
    // so the admin can manually enter the expiry date. This is critical for
    // important documents where the alert depends on the date being stored.
    setOcrModalData(ocrData);
    setOcrModalOpen(true);
  };

  // Handle OCR result confirmation
  const handleOcrConfirm = () => {
    if (!ocrModalData) return;
    
    const { docType, expiryDate, birthDate, rijksregisternr } = ocrModalData;
    const updates = {};
    const messages = [];
    
    if (docType === 'identity_card' && expiryDate) {
      updates.id_expiry_date = expiryDate;
      messages.push(`Kimlik son kullanma tarihi: ${new Date(expiryDate).toLocaleDateString('tr-TR')}`);
    } else if (docType === 'driver_license' && expiryDate) {
      updates.license_expiry_date = expiryDate;
      messages.push(`Ehliyet son kullanma tarihi: ${new Date(expiryDate).toLocaleDateString('tr-TR')}`);
    } else if (docType === 'bestuurdersattest' && expiryDate) {
      updates.driver_attest_expiry_date = expiryDate;
      messages.push(`Sürücü Belgesi son kullanma tarihi: ${new Date(expiryDate).toLocaleDateString('tr-TR')}`);
    }
    
    if (birthDate && docType !== 'bestuurdersattest') {
      updates.geboortedatum = birthDate;
      messages.push(`Doğum tarihi: ${new Date(birthDate).toLocaleDateString('tr-TR')}`);
    }

    if (rijksregisternr && docType !== 'bestuurdersattest') {
      updates.rijksregisternummer = rijksregisternr;
      messages.push(`Rijksregisternr: ${rijksregisternr}`);
    }
    
    if (Object.keys(updates).length > 0) {
      // Auto-persist the confirmed OCR values immediately so expiry alert scheduler
      // can pick them up without requiring the admin to click "Kaydet" later.
      const nextFormData = { ...formData, ...updates };
      setFormData(nextFormData);
      toast.success(`OCR verileri uygulandı: ${messages.join(', ')}`);
      (async () => {
        try {
          await api.put(`/employee-profile/user/${userId}`, nextFormData);
          fetchUserProfile();
        } catch (err) {
          console.error('Auto-save after OCR failed:', err);
          toast.error('Tarih otomatik kaydedilemedi, lütfen "Kaydet" butonuna basın');
        }
      })();
    }
    
    setOcrModalOpen(false);
    setOcrModalData(null);
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const response = await api.put(`/employee-profile/user/${userId}`, formData);
      if (response.data.success) {
        toast.success(t('employeeProfile.profileSaved'));
        fetchUserProfile();
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(t('employeeProfile.profileSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleEmploymentDateChange = async (field, value) => {
    try {
      const updateData = {
        employment_start_date: targetUser?.employment_start_date || null,
        employment_end_date: targetUser?.employment_end_date || null,
        [field]: value || null
      };
      
      const response = await api.put(`/admin/users/${userId}/employment-dates`, updateData);
      if (response.data.success) {
        setTargetUser(prev => ({ ...prev, [field]: value || null }));
        toast.success(t('employeeProfile.employmentDatesUpdated') || 'Tarihler güncellendi');
      }
    } catch (error) {
      console.error('Error updating employment dates:', error);
      toast.error(t('employeeProfile.employmentDatesError') || 'Tarihler güncellenemedi');
    }
  };

  const handleUploadProfilePhoto = async (blob) => {
    try {
      setUploadingPhoto(true);
      const formDataUpload = new FormData();
      formDataUpload.append('files', blob, 'profile-photo.jpg');
      formDataUpload.append('file_names', 'Profil Fotoğrafı');
      formDataUpload.append('category', 'PERSONAL');
      formDataUpload.append('assigned_to', userId);
      formDataUpload.append('document_type', 'profile_photo');

      const response = await api.post('/documents/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(t('employeeProfile.photoUploaded') || 'Profil fotoğrafı yüklendi');
        // Create local URL for immediate display
        const localUrl = URL.createObjectURL(blob);
        setProfilePhoto(localUrl);
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      toast.error(t('employeeProfile.photoUploadError') || 'Fotoğraf yüklenemedi');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Upload important document (ID, Passport, Driver's License)
  const handleUploadImportantDoc = async (file, docType) => {
    if (!file) return;
    
    try {
      setUploadingImportantDoc(true);
      const formDataUpload = new FormData();
      formDataUpload.append('files', file);
      
      // Map document type to display name
      const docNames = {
        'identity_card': t('employeeProfile.identityCard') || 'Kimlik Kartı',
        'passport': t('employeeProfile.passport') || 'Pasaport',
        'driver_license': t('employeeProfile.driverLicense') || 'Ehliyet'
      };
      
      formDataUpload.append('file_names', docNames[docType] || docType);
      formDataUpload.append('category', 'PERSONAL');
      formDataUpload.append('assigned_to', userId);
      formDataUpload.append('document_type', docType);

      const response = await api.post('/documents/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const uploadedIds = (response.data.data || []).map(d => d.id).filter(Boolean);
        showUploadThumbnailToast(
          [file],
          docNames[docType] || docType,
          null,
          { ids: uploadedIds, onComplete: fetchDocuments },
        );
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error uploading important document:', error);
      toast.error(t('employeeProfile.documentUploadError') || 'Belge yüklenemedi');
    } finally {
      setUploadingImportantDoc(false);
    }
  };

  const handleImportantDocSelect = (e, docType) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUploadImportantDoc(file, docType);
    }
    // Reset input
    e.target.value = '';
  };

  const handleAddFinanceEntry = async () => {
    if (!financeForm.entry_type || !financeForm.amount || !financeForm.date) {
      toast.error(t('employeeProfile.requiredFields'));
      return;
    }

    try {
      setSavingFinance(true);
      const response = await api.post(`/employee-profile/finance/${userId}`, {
        entry_type: financeForm.entry_type,
        amount: parseFloat(financeForm.amount),
        date: financeForm.date,
        description: financeForm.description
      });

      if (response.data.success) {
        toast.success(t('employeeProfile.profileSaved'));
        setFinanceDialog(false);
        setFinanceForm({ entry_type: '', amount: '', date: new Date().toISOString().split('T')[0], description: '' });
        fetchFinance();
      }
    } catch (error) {
      console.error('Error adding finance entry:', error);
      toast.error(t('employeeProfile.profileSaveError'));
    } finally {
      setSavingFinance(false);
    }
  };

  const handleDeleteFinanceEntry = async () => {
    if (!deleteFinanceEntry) return;
    try {
      const response = await api.delete(`/employee-profile/finance/entry/${deleteFinanceEntry.id}`);
      if (response.data.success) {
        toast.success(t('employeeProfile.documentDeleted'));
        fetchFinance();
      }
    } catch (error) {
      console.error('Error deleting finance entry:', error);
      toast.error(t('employeeProfile.documentDeleteError'));
    } finally {
      setDeleteFinanceEntry(null);
    }
  };

  const handleDownloadDocument = async (doc) => {
    const ok = await downloadDocument(api, doc);
    if (!ok) toast.error(t('employeeProfile.downloadError'));
  };

  // Handle document preview - uses unified DocumentPreviewModal for all file types
  const handleDocPreview = (doc) => {
    if (!doc) return;
    setPreviewDoc(doc);
  };

  const handleDocFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedDocFiles(prev => [...prev, ...files]);
    }
  };

  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files || []);
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xlsx', 'xls'].includes(ext);
    });
    
    if (validFiles.length > 0) {
      setSelectedDocFiles(prev => [...prev, ...validFiles]);
    }
    
    if (validFiles.length < files.length) {
      toast.warning(t('employeeProfile.invalidFileType') || 'Bazı dosyalar desteklenmeyen formatta');
    }
  };

  // Remove a file from the list
  const removeDocFile = (index) => {
    setSelectedDocFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle tab-level drag events (for Documents tab)
  const handleTabDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTabDragging(true);
  };

  const handleTabDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set false if leaving the actual drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsTabDragging(false);
    }
  };

  const handleTabDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTabDragging(false);
    
    const files = Array.from(e.dataTransfer.files || []);
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xlsx', 'xls'].includes(ext);
    });
    
    if (validFiles.length > 0) {
      setSelectedDocFiles(validFiles);
      setUploadDocDialog(true);
    }
    
    if (validFiles.length < files.length) {
      toast.warning(t('employeeProfile.invalidFileType') || 'Bazı dosyalar desteklenmeyen formatta');
    }
  };

  // ─── Ctrl+V (Paste) → Belgeleri pano üzerinden yükle ───────────────────────
  // Documents sekmesindeyken çalışır:
  //  · Yükleme dialog'u KAPALIysa → handleTabDrop davranışı: dosyaları
  //    selectedDocFiles'a koy + metadata dialog'unu aç (operatör doc_type/
  //    geçerlilik tarihi vb. doldurur).
  //  · Dialog AÇIKsa → handleDrop davranışı: mevcut listeye ekle (multi-paste).
  // Ekran görüntüleri için hook anlamlı pasted-<ts>.png adı atar.
  usePasteUpload((files) => {
    const validFiles = files.filter((f) => {
      const ext = (f.name || '').split('.').pop().toLowerCase();
      return ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xlsx', 'xls'].includes(ext);
    });
    if (validFiles.length === 0) return;
    if (uploadDocDialog) {
      // Dialog açık → mevcut seçime ekle (drop-in-dialog gibi).
      setSelectedDocFiles((prev) => [...prev, ...validFiles]);
    } else {
      // Dialog kapalı → tab-drop gibi: yeni seçim + dialog aç.
      setSelectedDocFiles(validFiles);
      setUploadDocDialog(true);
    }
  }, {
    enabled: activeTab === 'documents' && !!userId,
    accept: 'image/*,.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx',
  });

  const handleUploadDocument = async () => {
    if (selectedDocFiles.length === 0) {
      toast.error(t('employeeProfile.selectFile'));
      return;
    }

    try {
      setUploadingDoc(true);
      const formDataUpload = new FormData();
      
      // Append all files
      selectedDocFiles.forEach(file => {
        formDataUpload.append('files', file);
      });
      
      formDataUpload.append('category', docCategory);
      // Assign to this specific user
      formDataUpload.append('assigned_to', userId);
      // Custom name if provided (only for single file)
      if (customDocName.trim() && selectedDocFiles.length === 1) {
        formDataUpload.append('custom_names', customDocName.trim());
      }

      const response = await api.post('/documents/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const uploadedIds = (response.data.data || []).map(d => d.id).filter(Boolean);
        showUploadThumbnailToast(
          selectedDocFiles,
          customDocName.trim() && selectedDocFiles.length === 1
            ? customDocName.trim()
            : (t('employeeProfile.documents') || 'Belge'),
          null,
          { ids: uploadedIds, onComplete: fetchDocuments },
        );
        setUploadDocDialog(false);
        setSelectedDocFiles([]);
        setCustomDocName('');
        setDocCategory('PERSONAL');
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(t('employeeProfile.documentUploadError'));
    } finally {
      setUploadingDoc(false);
    }
  };

  // Delete document
  const handleDeleteDocument = async () => {
    if (!deleteDocDialog.doc) return;
    
    try {
      setDeletingDoc(true);
      const response = await api.delete(`/documents/${deleteDocDialog.doc.id}`);
      
      if (response.data.success) {
        toast.success(t('employeeProfile.documentDeleted') || 'Belge silindi');
        setDeleteDocDialog({ open: false, doc: null });
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(t('employeeProfile.documentDeleteError') || 'Belge silinemedi');
    } finally {
      setDeletingDoc(false);
    }
  };

  // Extract extension from a filename ("arbeid.pdf" -> ".pdf"; returns "" if none)
  const getFileExtension = (name) => {
    if (!name) return '';
    const idx = name.lastIndexOf('.');
    if (idx <= 0) return '';  // dotfile or no dot
    const ext = name.slice(idx);
    // Guard: accept only up to ~5 chars + alphanumerics (avoid "1.7.2" kind of strings)
    if (ext.length > 6 || !/^\.[A-Za-z0-9]+$/.test(ext)) return '';
    return ext;
  };

  // Smart-preserve original extension (if user stripped it)
  const applySmartExtension = (typedName, originalName) => {
    const trimmed = (typedName || '').trim();
    if (!trimmed) return '';
    const origExt = getFileExtension(originalName);
    if (!origExt) return trimmed;
    const typedExt = getFileExtension(trimmed);
    if (typedExt && typedExt.toLowerCase() === origExt.toLowerCase()) return trimmed;
    if (typedExt) return trimmed;  // user chose a different valid extension, respect it
    return trimmed + origExt;
  };

  // Edit document (category + file name)
  const handleOpenEditDoc = (doc) => {
    setEditDocCategory(doc.category || 'PERSONAL');
    setEditDocFileName(doc.file_name || doc.original_name || '');
    setEditDocDialog({ open: true, doc });
  };

  const handleEditDocument = async () => {
    if (!editDocDialog.doc) return;

    const newName = editDocFileName.trim();
    if (!newName) {
      toast.error('Dosya adı boş olamaz');
      return;
    }

    // Smart-rename: preserve original extension if user omitted it.
    const finalName = applySmartExtension(
      newName,
      editDocDialog.doc.original_name || editDocDialog.doc.file_name || ''
    );

    // Duplicate check: same final name on another doc for this user.
    const duplicate = documents.find(
      d => d.id !== editDocDialog.doc.id &&
           (d.file_name || '').toLowerCase() === finalName.toLowerCase()
    );
    if (duplicate) {
      const ok = window.confirm(
        `"${finalName}" adıyla başka bir belge zaten var.\n\nYine de kaydedilsin mi?`
      );
      if (!ok) return;
    }

    try {
      setEditingDoc(true);
      const response = await api.put(`/documents/${editDocDialog.doc.id}`, {
        category: editDocCategory,
        file_name: finalName,
      });

      if (response.data.success) {
        const renamed = finalName !== newName;
        toast.success(
          renamed
            ? `Belge güncellendi ("${finalName}" olarak kaydedildi)`
            : (t('employeeProfile.documentUpdated') || 'Belge güncellendi')
        );
        setEditDocDialog({ open: false, doc: null });
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error(t('employeeProfile.documentUpdateError') || 'Belge güncellenemedi');
    } finally {
      setEditingDoc(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('nl-BE');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!targetUser) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 dark:text-slate-400">{t('common.noData')}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('employeeProfile.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          {/* Profile Photo with Upload */}
          <div className="relative group">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-2xl font-bold overflow-hidden border-4 border-white shadow-lg">
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                formData.voornaam?.[0]?.toUpperCase() || targetUser?.full_name?.[0]?.toUpperCase() || 'U'
              )}
            </div>
            <button
              onClick={() => setPhotoCropperOpen(true)}
              className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
              data-testid="admin-change-photo-btn"
            >
              <Camera className="h-5 w-5 text-white" />
            </button>
            {uploadingPhoto && (
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                <Loader2 className="h-5 w-5 text-white animate-spin" />
              </div>
            )}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {formData.voornaam || formData.naam ? `${formData.voornaam} ${formData.naam}`.trim() : targetUser?.full_name || t('common.noData')}
            </h1>
            <p className="text-slate-500 dark:text-slate-400">{targetUser?.email}</p>
            <div className="flex flex-wrap gap-2 mt-1.5 items-center">
              <Badge variant="outline">{targetUser?.role}</Badge>
              {targetUser?.firm_name ? (
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 gap-1 border-0">
                  <Building2 className="h-3 w-3" />
                  {targetUser.firm_name}
                </Badge>
              ) : (
                <Badge className="bg-slate-100 text-slate-500 border-0">Firma atanmamış</Badge>
              )}
              {targetUser?.employment_end_date && (
                <Badge className="bg-red-600 text-white hover:bg-red-600 border-0 gap-1 animate-pulse" data-testid="profile-exit-badge">
                  <AlertTriangle className="h-3 w-3" />
                  İşten Ayrıldı · {new Date(targetUser.employment_end_date).toLocaleDateString('tr-TR')}
                </Badge>
              )}
            </div>
            <button 
              onClick={() => setPhotoCropperOpen(true)}
              className="text-xs text-blue-600 hover:text-blue-700 mt-1 flex items-center gap-1"
            >
              <Camera className="h-3 w-3" />
              {t('employeeProfile.changePhoto') || 'Fotoğrafı Değiştir'}
            </button>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Impersonate Button */}
          {targetUser && targetUser.role !== 'SUPER_ADMIN' && currentUser?.id !== userId && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleImpersonate}
              className="gap-2 text-purple-600 border-purple-200 hover:bg-purple-50 hover:border-purple-300"
              data-testid="impersonate-btn"
            >
              <LogIn className="h-4 w-4" />
              Paneline Gir
            </Button>
          )}
          
          <div className="text-right">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('employeeProfile.profileCompletion')}</p>
            <div className="flex items-center gap-2">
              <Progress value={profileCompletion} className="w-32 h-2" />
              <span className="text-sm font-medium">{profileCompletion}%</span>
            </div>
          </div>
          {profileCompletion === 100 ? (
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle2 className="h-3 w-3 mr-1" /> {t('employeeProfile.complete')}
            </Badge>
          ) : (
            <Badge className="bg-yellow-100 text-yellow-700">
              <AlertCircle className="h-3 w-3 mr-1" /> {t('employeeProfile.incomplete')}
            </Badge>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full lg:w-auto">
          <TabsTrigger value="personal" className="gap-2">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">{t('employeeProfile.personalInfo')}</span>
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">{t('employeeProfile.documents')}</span>
          </TabsTrigger>
          {canViewPackagesTab && (
          <TabsTrigger value="packages" className="gap-2" data-testid="tab-packages">
            <Package className="h-4 w-4" />
            <span className="hidden sm:inline">{t('employeeProfile.packagesTab') || 'Paketler'}</span>
          </TabsTrigger>
          )}
          {canViewLoonbriefsTab && (
          <TabsTrigger value="loonbriefs" className="gap-2" data-testid="tab-loonbriefs">
            <Wallet className="h-4 w-4" />
            <span className="hidden sm:inline">{t('employeeProfile.loonbriefsTab') || 'Bodrolar'}</span>
          </TabsTrigger>
          )}
          <TabsTrigger value="history" className="gap-2">
            <Calendar className="h-4 w-4" />
            <span className="hidden sm:inline">{t('employeeProfile.history')}</span>
          </TabsTrigger>
          {canViewFinanceTab && (
          <TabsTrigger value="finance" className="gap-2" data-testid="tab-finance">
            <Euro className="h-4 w-4" />
            <span className="hidden sm:inline">{t('employeeProfile.finance')}</span>
          </TabsTrigger>
          )}
          {canViewNotesTab && (
          <TabsTrigger value="notes" className="gap-2" data-testid="tab-notes">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">{t('employeeProfile.notesList') || 'Notlar'}</span>
          </TabsTrigger>
          )}
          <TabsTrigger value="firm-history" className="gap-2" data-testid="firm-history-tab">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Firma Geçmişi</span>
          </TabsTrigger>
        </TabsList>

        {/* Personal Info Tab - Same as EmployeeProfile but admin can edit */}
        <TabsContent value="personal" className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                {t('employeeProfile.personalInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="voornaam">{t('employeeProfile.firstName')} *</Label>
                  <Input
                    id="voornaam"
                    value={formData.voornaam}
                    onChange={(e) => setFormData({ ...formData, voornaam: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="naam">{t('employeeProfile.lastName')} *</Label>
                  <Input
                    id="naam"
                    value={formData.naam}
                    onChange={(e) => setFormData({ ...formData, naam: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="geboortedatum">{t('employeeProfile.birthDate')} *</Label>
                  <Input
                    id="geboortedatum"
                    type="date"
                    value={formData.geboortedatum}
                    onChange={(e) => setFormData({ ...formData, geboortedatum: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nationaliteit">{t('employeeProfile.nationality')} *</Label>
                  <Select value={formData.nationaliteit} onValueChange={(v) => setFormData({ ...formData, nationaliteit: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('common.select') || 'Seçiniz'} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {NATIONALITIES.map((nat) => (
                        <SelectItem key={nat.code} value={nat.code} disabled={nat.disabled}>
                          {nat.flag} {nat[language] || nat.tr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rijksregisternummer">{t('employeeProfile.nationalId')}</Label>
                  <Input
                    id="rijksregisternummer"
                    data-testid="rijksregisternummer-input"
                    placeholder={getPlaceholder(formData.nationaliteit)}
                    value={formData.rijksregisternummer}
                    onChange={(e) => {
                      const cc = formData.nationaliteit;
                      const raw = stripDigits(e.target.value);
                      const max = getMaxDigits(cc);
                      if (raw.length <= max) {
                        const shouldFormat = (cc === 'BE' && raw.length === 11) || (cc === 'TR' && raw.length === 11);
                        setFormData({ ...formData, rijksregisternummer: shouldFormat ? formatNationalId(raw, cc) : e.target.value });
                      }
                    }}
                    className={(() => {
                      const cc = formData.nationaliteit;
                      const digits = stripDigits(formData.rijksregisternummer);
                      if (!digits) return '';
                      if ((cc === 'BE' || cc === 'TR') && digits.length < 11) return '';
                      const v = validateNationalId(formData.rijksregisternummer, cc, formData.geboortedatum);
                      if (!v.valid) return 'border-red-500 focus-visible:ring-red-500';
                      if (v.warning) return 'border-amber-500 focus-visible:ring-amber-500';
                      if (v.info) return 'border-green-500 focus-visible:ring-green-500';
                      return '';
                    })()}
                  />
                  {(() => {
                    const cc = formData.nationaliteit;
                    const digits = stripDigits(formData.rijksregisternummer);
                    if (!digits) return null;
                    if ((cc === 'BE' || cc === 'TR') && digits.length < 11) return null;
                    const result = validateNationalId(formData.rijksregisternummer, cc, formData.geboortedatum);
                    if (result.error) return <p className="text-xs text-red-600 mt-1" data-testid="rrn-error">{result.error}</p>;
                    if (result.warning) return <p className="text-xs text-amber-600 mt-1" data-testid="rrn-warning">{result.warning}</p>;
                    if (result.info) return <p className="text-xs text-green-600 mt-1" data-testid="rrn-valid">{result.info}</p>;
                    return null;
                  })()}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="burgerlijke_staat">{t('employeeProfile.maritalStatus')}</Label>
                  <Select
                    value={formData.burgerlijke_staat}
                    onValueChange={(v) => setFormData({ ...formData, burgerlijke_staat: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(MARITAL_STATUS_KEYS).map(([key, i18nKey]) => (
                        <SelectItem key={key} value={key}>{t(`employeeProfile.${i18nKey}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="id_expiry_date" className="flex items-center gap-2">
                    <IdCard className="h-4 w-4 text-blue-500" />
                    {t('employeeProfile.idExpiryDate') || 'Kimlik Son Kullanma Tarihi'}
                  </Label>
                  <Input
                    id="id_expiry_date"
                    type="date"
                    value={formData.id_expiry_date || ''}
                    onChange={(e) => setFormData({ ...formData, id_expiry_date: e.target.value })}
                    data-testid="id-expiry-date"
                  />
                  <p className="text-xs text-slate-500">{t('employeeProfile.ocrHint') || 'Kimlik yüklerken otomatik doldurulur'}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="license_expiry_date" className="flex items-center gap-2">
                    <Car className="h-4 w-4 text-green-500" />
                    {t('employeeProfile.licenseExpiryDate') || 'Ehliyet Son Kullanma Tarihi'}
                  </Label>
                  <Input
                    id="license_expiry_date"
                    type="date"
                    value={formData.license_expiry_date || ''}
                    onChange={(e) => setFormData({ ...formData, license_expiry_date: e.target.value })}
                    data-testid="license-expiry-date"
                  />
                  <p className="text-xs text-slate-500">{t('employeeProfile.ocrHint') || 'Ehliyet yüklerken otomatik doldurulur'}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driver_attest_expiry_date" className="flex items-center gap-2">
                    <FileCheck2 className="h-4 w-4 text-purple-500" />
                    Sürücü Belgesi (Bestuurdersattest) Bitiş Tarihi
                  </Label>
                  <Input
                    id="driver_attest_expiry_date"
                    type="date"
                    value={formData.driver_attest_expiry_date || ''}
                    onChange={(e) => setFormData({ ...formData, driver_attest_expiry_date: e.target.value })}
                    data-testid="driver-attest-expiry-date"
                  />
                  <p className="text-xs text-slate-500">{`Bestuurdersattest PDF'i yüklerken otomatik doldurulur. 14 ve 7 gün kala bildirim gönderilir.`}</p>
                </div>
              </div>

              {/* PostNL Codes — shown ABOVE Employment Dates per requirement */}
              <PostNLAssignedCard userId={userId} firms={firms} />

              {/* Employment Dates Section */}
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-600">
                {targetUser?.employment_end_date && (
                  <div className="mb-3 flex items-center gap-2 rounded-lg border-2 border-red-400 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm font-semibold text-red-700 dark:text-red-300" data-testid="profile-exit-banner">
                    <AlertTriangle className="h-4 w-4" />
                    Bu çalışan {new Date(targetUser.employment_end_date).toLocaleDateString('tr-TR')} tarihinde işten ayrıldı — belge ve profil uyarıları artık gösterilmiyor.
                    <RehireDialog user={targetUser} firms={firms} onDone={() => { setFirmHistory(null); fetchUserProfile(); }} />
                  </div>
                )}
                <Label className="flex items-center gap-2 mb-3">
                  <Calendar className="h-4 w-4 text-green-600" />
                  {t('employeeProfile.employmentDates') || 'İş Tarihleri'}
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="employment_start_date">{t('employeeProfile.employmentStartDate') || 'İşe Başlama Tarihi'}</Label>
                    <Input
                      id="employment_start_date"
                      type="date"
                      value={targetUser?.employment_start_date || ''}
                      onChange={(e) => handleEmploymentDateChange('employment_start_date', e.target.value)}
                      data-testid="employment-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="employment_end_date">{t('employeeProfile.employmentEndDate') || 'İşten Ayrılma Tarihi'}</Label>
                    <Input
                      id="employment_end_date"
                      type="date"
                      value={targetUser?.employment_end_date || ''}
                      onChange={(e) => handleEmploymentDateChange('employment_end_date', e.target.value)}
                      data-testid="employment-end-date"
                    />
                  </div>
                </div>
              </div>
              
              {/* Aliases / Lakaplar Section */}
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-600">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-purple-600" />
                    {t('employeeProfile.aliases') || 'Lakaplar / İkincil İsimler'}
                    <span className="text-xs text-slate-400 font-normal">({t('common.optional') || 'opsiyonel'})</span>
                  </Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                    {t('employeeProfile.aliasesDescription') || 'Bordro PDF eşleştirmesi için kullanılır. Örn: GEMALMAZ HUSAMETTIN, H.GEMALMAZ'}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(formData.aliases || []).map((alias, idx) => (
                      <Badge key={idx} variant="secondary" className="pl-3 pr-1 py-1 bg-purple-50 text-purple-700 border-purple-200">
                        {alias}
                        <button
                          type="button"
                          onClick={() => {
                            const newAliases = formData.aliases.filter((_, i) => i !== idx);
                            setFormData({ ...formData, aliases: newAliases });
                          }}
                          className="ml-2 hover:bg-purple-200 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      id="alias-input"
                      placeholder={t('employeeProfile.addAliasPlaceholder') || 'Yeni lakap ekle...'}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const value = e.target.value.trim();
                          if (value && !formData.aliases.some(a => a.toLowerCase() === value.toLowerCase())) {
                            setFormData({ ...formData, aliases: [...(formData.aliases || []), value] });
                            e.target.value = '';
                          }
                        }
                      }}
                      className="max-w-xs"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const input = document.getElementById('alias-input');
                        const value = input.value.trim();
                        if (value && !formData.aliases.some(a => a.toLowerCase() === value.toLowerCase())) {
                          setFormData({ ...formData, aliases: [...(formData.aliases || []), value] });
                          input.value = '';
                        }
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Mart 01, 2026 — PostNL Paket Eşleştirmesi Kapat Toggle */}
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-600">
                <label className="flex items-start gap-3 cursor-pointer group" htmlFor="hide-postnl-toggle">
                  <input
                    id="hide-postnl-toggle"
                    type="checkbox"
                    checked={!!formData.hide_postnl_packages}
                    onChange={(e) => setFormData({ ...formData, hide_postnl_packages: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                    data-testid="toggle-hide-postnl-packages"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium flex items-center gap-2">
                      <Package className="h-4 w-4 text-amber-600" />
                      PostNL Paket Eşleştirmesini Kapat
                      <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-200 text-amber-700">
                        Yanlış ad/soyad eşleşmesi için
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Açılırsa <b>Paketlerim</b> sayfası ve <b>Çalışan Özeti</b> paket toplamları
                      bu kullanıcı için gösterilmez. (Bordro eşleştirmesi etkilenmez.)
                      <br />
                      <span className="text-amber-600">
                        Örn: "Cenk Mahmud" soy ismi başka bir sürücünün adıyla ("Mahmud, C.")
                        çakışıyorsa bu seçeneği açın.
                      </span>
                    </p>
                  </div>
                </label>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-green-600" />
                {t('employeeProfile.addressInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2 lg:col-span-2">
                  <Label>{t('employeeProfile.street')} *</Label>
                  <Input
                    value={formData.straat}
                    onChange={(e) => setFormData({ ...formData, straat: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('employeeProfile.houseNumber')} *</Label>
                  <Input
                    value={formData.huisnummer}
                    onChange={(e) => setFormData({ ...formData, huisnummer: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('employeeProfile.postalCode')} *</Label>
                  <Input
                    value={formData.postcode}
                    onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  />
                </div>
                <div className="space-y-2 lg:col-span-2">
                  <Label>{t('employeeProfile.city')} *</Label>
                  <Input
                    value={formData.woonplaats}
                    onChange={(e) => setFormData({ ...formData, woonplaats: e.target.value })}
                  />
                </div>
                <div className="space-y-2 lg:col-span-2">
                  <Label>{t('employeeProfile.country')} *</Label>
                  <Select value={formData.land} onValueChange={(v) => setFormData({ ...formData, land: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('common.select') || 'Seçiniz'} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.code} disabled={country.disabled}>
                          {country.flag} {country[language] || country.tr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact & Bank */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Phone className="h-5 w-5 text-purple-600" />
                {t('employeeProfile.contactBankInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label>{t('employeeProfile.phone')} *</Label>
                    {/* WhatsApp hızlı aksiyon — süper admin mobil numarayı
                        tek tıkla WhatsApp Web'de açar. Numara boşsa ya da
                        geçersizse buton gizlenir. wa.me rakam dışı her şeyi
                        atar (+, boşluk, tire), o yüzden biz de öyle temizliyoruz. */}
                    {(() => {
                      const digits = String(formData.gsm || '').replace(/\D/g, '');
                      if (!digits || digits.length < 6) return null;
                      return (
                        <a
                          href={`https://wa.me/${digits}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 transition-colors"
                          title={t('employeeProfile.openInWhatsapp') || 'WhatsApp Web\'de aç'}
                          data-testid="employee-phone-whatsapp"
                        >
                          <MessageCircle className="h-3 w-3" />
                          WhatsApp
                        </a>
                      );
                    })()}
                  </div>
                  <Input
                    value={formData.gsm}
                    onChange={(e) => setFormData({ ...formData, gsm: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('employeeProfile.emailAddress')} *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('employeeProfile.bankAccount')} *</Label>
                  <Input
                    value={formData.bankrekening_iban}
                    onChange={(e) => setFormData({ ...formData, bankrekening_iban: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fiscal Situation */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-orange-600" />
                {t('employeeProfile.fiscalInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label className="text-base font-medium">{t('employeeProfile.childrenDependent')}</Label>
                <RadioGroup
                  value={formData.fiscale_gezinssituatie.kinderen_ten_laste ? 'ja' : 'neen'}
                  onValueChange={(v) => setFormData({
                    ...formData,
                    fiscale_gezinssituatie: {
                      ...formData.fiscale_gezinssituatie,
                      kinderen_ten_laste: v === 'ja',
                      aantal_kinderen: v === 'ja' ? formData.fiscale_gezinssituatie.aantal_kinderen : null
                    }
                  })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ja" id="admin-kinderen-ja" />
                    <Label htmlFor="admin-kinderen-ja">{t('common.yes')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="neen" id="admin-kinderen-neen" />
                    <Label htmlFor="admin-kinderen-neen">{t('common.no')}</Label>
                  </div>
                </RadioGroup>
                {formData.fiscale_gezinssituatie.kinderen_ten_laste && (
                  <Input
                    type="number"
                    min="1"
                    className="w-32 ml-6"
                    placeholder={t('employeeProfile.numberOfChildren')}
                    value={formData.fiscale_gezinssituatie.aantal_kinderen || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      fiscale_gezinssituatie: {
                        ...formData.fiscale_gezinssituatie,
                        aantal_kinderen: parseInt(e.target.value) || null
                      }
                    })}
                  />
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-base font-medium">{t('employeeProfile.spouseDependent')}</Label>
                <RadioGroup
                  value={formData.fiscale_gezinssituatie.echtgenoot_ten_laste ? 'ja' : 'neen'}
                  onValueChange={(v) => setFormData({
                    ...formData,
                    fiscale_gezinssituatie: {
                      ...formData.fiscale_gezinssituatie,
                      echtgenoot_ten_laste: v === 'ja'
                    }
                  })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ja" id="admin-echtgenoot-ja" />
                    <Label htmlFor="admin-echtgenoot-ja">{t('common.yes')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="neen" id="admin-echtgenoot-neen" />
                    <Label htmlFor="admin-echtgenoot-neen">{t('common.no')}</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-medium">{t('employeeProfile.otherDependents')}</Label>
                <RadioGroup
                  value={formData.fiscale_gezinssituatie.andere_personen_ten_laste ? 'ja' : 'neen'}
                  onValueChange={(v) => setFormData({
                    ...formData,
                    fiscale_gezinssituatie: {
                      ...formData.fiscale_gezinssituatie,
                      andere_personen_ten_laste: v === 'ja',
                      aantal_andere_personen: v === 'ja' ? formData.fiscale_gezinssituatie.aantal_andere_personen : null
                    }
                  })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ja" id="admin-andere-ja" />
                    <Label htmlFor="admin-andere-ja">{t('common.yes')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="neen" id="admin-andere-neen" />
                    <Label htmlFor="admin-andere-neen">{t('common.no')}</Label>
                  </div>
                </RadioGroup>
                {formData.fiscale_gezinssituatie.andere_personen_ten_laste && (
                  <Input
                    type="number"
                    min="1"
                    className="w-32 ml-6"
                    placeholder={t('employeeProfile.numberOfOtherDependents')}
                    value={formData.fiscale_gezinssituatie.aantal_andere_personen || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      fiscale_gezinssituatie: {
                        ...formData.fiscale_gezinssituatie,
                        aantal_andere_personen: parseInt(e.target.value) || null
                      }
                    })}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSaveProfile} disabled={saving} size="lg">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {t('employeeProfile.saveProfile')}
            </Button>
          </div>

          {/* Atanmış araç(lar) — operatörün başkasının profiline baktığında
              o kişiye atanmış güncel plakayı + geçmiş atamaları görür. */}
          <UserVehicleHistory userId={userId} />
          <UserAssignmentHistory userId={userId} />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent
          value="documents"
          className="space-y-6"
        >
          {/* Important Documents Section - New Component with Front/Back Support */}
          <ImportantDocumentsCard
            documents={documents}
            userId={userId}
            onRefresh={fetchDocuments}
            onOcrComplete={handleOcrComplete}
            t={t}
          />

          {/* All Documents */}
          <Card
            className={`transition-all ${isTabDragging ? 'ring-4 ring-blue-500 ring-opacity-50' : ''}`}
            onDragOver={handleTabDragOver}
            onDragLeave={handleTabDragLeave}
            onDrop={handleTabDrop}
          >
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                {t('employeeProfile.documentsOf')} {targetUser?.full_name || targetUser?.first_name}
              </CardTitle>
              <Button onClick={() => setUploadDocDialog(true)}>
                <Upload className="h-4 w-4 mr-2" />
                {t('employeeProfile.uploadDocument')}
              </Button>
            </CardHeader>
            <CardContent>
              {/* Drag & Drop Hint */}
              <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-dashed border-slate-300 dark:border-slate-600">
                <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  {t('employeeProfile.dragDropHint') || 'İpucu: Dosyaları bu alana sürükleyip bırakarak hızlıca yükleyebilirsiniz'}
                  <span className="ml-1">·</span>
                  <span><span className="font-medium text-blue-600 dark:text-blue-400">Ctrl+V</span> ile de yapıştırabilirsiniz</span>
                </p>
              </div>
              
              {documentsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t('employeeProfile.noDocuments')}</p>
                  <Button variant="outline" className="mt-4" onClick={() => setUploadDocDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t('employeeProfile.firstUpload')}
                  </Button>
                </div>
              ) : (
                <>
                {(() => {
                  // Apply filters first, then sort newest-first, then paginate
                  const q = docSearch.trim();
                  const filteredDocs = documents.filter((doc) => {
                    if (docCategoryFilter === 'PERSONAL' && doc.category !== 'PERSONAL') return false;
                    if (docCategoryFilter === 'COMPANY' && doc.category !== 'COMPANY') return false;
                    if (docCategoryFilter === 'IMPORTANT' && !IMPORTANT_DOC_TYPES.includes(doc.document_type)) return false;
                    if (!q) return true;
                    return (
                      turkishIncludes(doc.file_name || '', q) ||
                      turkishIncludes(doc.original_name || '', q) ||
                      turkishIncludes(doc.category || '', q) ||
                      turkishIncludes(doc.file_type || '', q) ||
                      turkishIncludes(doc.document_type || '', q)
                    );
                  });
                  const sortedDocs = [...filteredDocs].sort((a, b) => {
                    const da = a.created_at ? new Date(a.created_at).getTime() : 0;
                    const dbb = b.created_at ? new Date(b.created_at).getTime() : 0;
                    return dbb - da;
                  });
                  const totalPages = Math.max(1, Math.ceil(sortedDocs.length / docPageSize));
                  const safePage = Math.min(docPage, totalPages);
                  const startIdx = (safePage - 1) * docPageSize;
                  const visibleDocs = sortedDocs.slice(startIdx, startIdx + docPageSize);
                  const totalAll = documents.length;

                  const chipBase = 'px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors border';
                  const chipActive = 'bg-blue-600 text-white border-blue-600';
                  const chipIdle = 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-blue-400';
                  const filters = [
                    { key: 'ALL', label: `Tümü (${totalAll})` },
                    { key: 'IMPORTANT', label: `Önemli (${documents.filter(d => IMPORTANT_DOC_TYPES.includes(d.document_type)).length})` },
                    { key: 'PERSONAL', label: `Kişisel (${documents.filter(d => d.category === 'PERSONAL').length})` },
                    { key: 'COMPANY', label: `Firma (${documents.filter(d => d.category === 'COMPANY').length})` },
                  ];

                  return (
                    <>
                {/* Filter row: search + category chips */}
                <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4" data-testid="documents-filter-bar">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Belge adı, kategori veya türle ara..."
                      value={docSearch}
                      onChange={(e) => { setDocSearch(e.target.value); setDocPage(1); }}
                      className="pl-9 h-9"
                      data-testid="documents-search"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {filters.map(f => (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => { setDocCategoryFilter(f.key); setDocPage(1); }}
                        className={`${chipBase} ${docCategoryFilter === f.key ? chipActive : chipIdle}`}
                        data-testid={`documents-filter-${f.key.toLowerCase()}`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common.name')}</TableHead>
                      <TableHead>{t('employeeProfile.category')}</TableHead>
                      <TableHead>{t('common.type')}</TableHead>
                      <TableHead>{t('common.date')}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleDocs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                          Arama veya filtrele eşleşen belge yok.
                        </TableCell>
                      </TableRow>
                    ) : visibleDocs.map((doc) => {
                      const isImportant = IMPORTANT_DOC_TYPES.includes(doc.document_type);
                      return (
                      <TableRow key={doc.id} className={isImportant ? 'bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100/70 dark:hover:bg-amber-900/40' : ''}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {isImportant && <IdCard className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
                            {doc.file_name || doc.original_name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={isImportant ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800/60' : ''}>{doc.category || 'DOC'}</Badge>
                        </TableCell>
                        <TableCell>{doc.file_type?.toUpperCase() || '-'}</TableCell>
                        <TableCell>{formatDate(doc.created_at)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleDocPreview(doc)} title={t('common.view') || 'Önizle'}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDownloadDocument(doc)} title={t('common.download') || 'İndir'}>
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleOpenEditDoc(doc)} title={t('common.edit') || 'Düzenle'}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteDocDialog({ open: true, doc })} 
                              title={t('common.delete') || 'Sil'}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>

                {/* Pagination controls */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 text-sm" data-testid="documents-pagination">
                  <div className="flex items-center gap-3 text-slate-500">
                    <span>
                      Toplam <span className="font-semibold text-slate-700 dark:text-slate-200">{sortedDocs.length}</span> belge
                      {sortedDocs.length > 0 && (
                        <>
                          {' '}· Gösterilen <span className="font-semibold">{startIdx + 1}-{Math.min(startIdx + docPageSize, sortedDocs.length)}</span>
                        </>
                      )}
                    </span>
                    <span className="hidden sm:inline text-slate-300">|</span>
                    <div className="flex items-center gap-2">
                      <span>Sayfa başına:</span>
                      <select
                        value={docPageSize}
                        onChange={(e) => { setDocPageSize(Number(e.target.value)); setDocPage(1); }}
                        className="h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                        data-testid="documents-page-size"
                      >
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDocPage(1)}
                      disabled={safePage <= 1}
                      data-testid="documents-page-first"
                    >
                      « İlk
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDocPage(p => Math.max(1, p - 1))}
                      disabled={safePage <= 1}
                      data-testid="documents-page-prev"
                    >
                      ‹ Önceki
                    </Button>
                    <span className="px-3 font-medium tabular-nums">
                      {safePage} / {totalPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDocPage(p => Math.min(totalPages, p + 1))}
                      disabled={safePage >= totalPages}
                      data-testid="documents-page-next"
                    >
                      Sonraki ›
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDocPage(totalPages)}
                      disabled={safePage >= totalPages}
                      data-testid="documents-page-last"
                    >
                      Son »
                    </Button>
                  </div>
                </div>
                    </>
                  );
                })()}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Packages (Paketler) Tab - Admin Version with amounts */}
        {canViewPackagesTab && (
        <TabsContent value="packages" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap items-center gap-3">
                {/* Quick Selection Buttons */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'today', label: t('employeeProfile.today') || 'Bugün', getValue: () => {
                      // Local date — toISOString TR (UTC+3) timezone'unda 1 gün öne kayar
                      const d = new Date();
                      const today = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                      return { start: today, end: today };
                    }},
                    { key: 'yesterday', label: t('employeeProfile.yesterday') || 'Dün', getValue: () => {
                      const d = new Date(Date.now() - 86400000);
                      const yesterday = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                      return { start: yesterday, end: yesterday };
                    }},
                    { key: 'week', label: t('employeeProfile.thisWeek') || 'Bu Hafta', getValue: () => {
                      const now = new Date();
                      const dayOfWeek = now.getDay();
                      const startOfWeek = new Date(now);
                      startOfWeek.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
                      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                      return { start: fmt(startOfWeek), end: fmt(now) };
                    }},
                    { key: 'month', label: t('employeeProfile.thisMonth') || 'Bu Ay', getValue: () => {
                      const now = new Date();
                      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                      return { start: fmt(startOfMonth), end: fmt(now) };
                    }},
                    { key: 'lastMonth', label: 'Geçen Ay', getValue: () => {
                      const now = new Date();
                      const startOfLast = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                      const endOfLast = new Date(now.getFullYear(), now.getMonth(), 0); // önceki ayın son günü
                      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                      return { start: fmt(startOfLast), end: fmt(endOfLast) };
                    }},
                    { key: 'year', label: t('employeeProfile.thisYear') || 'Bu Yıl', getValue: () => {
                      const now = new Date();
                      const startOfYear = new Date(now.getFullYear(), 0, 1);
                      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                      return { start: fmt(startOfYear), end: fmt(now) };
                    }},
                  ].map((preset) => (
                    <Button
                      key={preset.key}
                      variant={selectedPackagePreset === preset.key ? "default" : "outline"}
                      size="sm"
                      className="px-3 py-1.5 text-xs h-8"
                      onClick={() => {
                        const { start, end } = preset.getValue();
                        setPackageStartDate(start);
                        setPackageEndDate(end);
                        setSelectedPackagePreset(preset.key);
                      }}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>

                <div className="h-6 w-px bg-slate-200 hidden sm:block" />

                {/* Date Range */}
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={packageStartDate}
                    onChange={(e) => {
                      setPackageStartDate(e.target.value);
                      setSelectedPackagePreset('');
                    }}
                    className="h-8 w-32 text-xs"
                  />
                  <span className="text-slate-400 text-xs">-</span>
                  <Input
                    type="date"
                    value={packageEndDate}
                    onChange={(e) => {
                      setPackageEndDate(e.target.value);
                      setSelectedPackagePreset('');
                    }}
                    className="h-8 w-32 text-xs"
                  />
                </div>

                {/* Region Filter */}
                <Select value={packageRegionFilter || "all"} onValueChange={(v) => setPackageRegionFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-8 w-32 text-xs">
                    <SelectValue placeholder={t('employeeProfile.allRegions') || 'Tüm Bölgeler'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('employeeProfile.allRegions') || 'Tüm Bölgeler'}</SelectItem>
                    {packagesRegions.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Filter Button */}
                <Button onClick={fetchPackages} disabled={packagesLoading} size="sm" className="h-8">
                  {packagesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {packagesLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : packages ? (
            <>
              {/* Summary Cards - Admin sees amounts */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card className="shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Package className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-lg font-semibold text-blue-600 truncate">
                          {packages.summary?.total_quantity?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t('employeeProfile.totalPackages') || 'Toplam Paket'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-4 w-4 text-emerald-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-lg font-semibold text-emerald-600">
                          {packages.summary?.total_days || 0}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t('employeeProfile.workedDays') || 'Çalışılan Gün'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="h-4 w-4 text-amber-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-lg font-semibold text-amber-600">
                          {packages.summary?.avg_daily || 0}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t('employeeProfile.dailyAvg') || 'Günlük Ort.'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-4 w-4 text-purple-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-lg font-semibold text-purple-600">
                          {packages.summary?.regions?.length || 0}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t('employeeProfile.region') || 'Bölge'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {/* Admin-only: Total Amount */}
                {packages.summary?.total_amount !== undefined && (
                  <Card className="shadow-sm bg-green-50 border-green-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <Euro className="h-4 w-4 text-green-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-lg font-semibold text-green-600 truncate">
                            € {packages.summary?.total_amount?.toLocaleString('nl-NL', {minimumFractionDigits: 2}) || '0,00'}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{t('employeeProfile.totalAmount') || 'Toplam Tutar'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Charts Section */}
              {packages.daily_breakdown?.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Daily Packages Bar Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-blue-600" />
                        {t('employeeProfile.dailyPackageDist') || 'Günlük Paket Dağılımı'}
                      </CardTitle>
                      <CardDescription>{t('employeeProfile.last30DaysPackages') || 'Son 30 günün paket sayısı'}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]" style={{ minHeight: 300, minWidth: 0 }}>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart 
                            data={packages.daily_breakdown.slice(0, 30).reverse().map(d => ({
                              date: new Date(d.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }),
                              paket: d.total_quantity,
                              tutar: d.total_amount || 0
                            }))}
                            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis 
                              dataKey="date" 
                              tick={{ fontSize: 11 }} 
                              angle={-45} 
                              textAnchor="end" 
                              height={60}
                            />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: 'white', 
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                              }}
                              formatter={(value, name) => [
                                name === 'paket' ? `${value} ${t('employeeProfile.packageCount') || 'adet'}` : `€${value.toFixed(2)}`,
                                name === 'paket' ? (t('employeeProfile.packageLabel') || 'Paket') : (t('employeeProfile.amountLabel') || 'Tutar')
                              ]}
                            />
                            <Bar dataKey="paket" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Region Distribution Pie Chart */}
                  {packages.regions_breakdown?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <MapPin className="h-5 w-5 text-purple-600" />
                          {t('employeeProfile.regionDist') || 'Bölge Dağılımı'}
                        </CardTitle>
                        <CardDescription>{t('employeeProfile.regionDistDesc') || 'Paketlerin bölgelere göre dağılımı'}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]" style={{ minHeight: 300, minWidth: 0 }}>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={packages.regions_breakdown.map((r, idx) => ({
                                  name: r.region,
                                  value: r.total_quantity,
                                  tutar: r.total_amount || 0
                                }))}
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                innerRadius={50}
                                paddingAngle={2}
                                dataKey="value"
                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                labelLine={true}
                              >
                                {packages.regions_breakdown.map((_, index) => (
                                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: 'white', 
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                                }}
                                formatter={(value, name, props) => [
                                  `${value.toLocaleString()} ${t('employeeProfile.packageLabel') || 'paket'}`,
                                  props.payload.name
                                ]}
                              />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Regions Breakdown - Admin sees amounts */}
              {packages.regions_breakdown?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-purple-600" />
                      {t('employeeProfile.regionDist') || 'Bölgelere Göre Dağılım'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50 dark:bg-slate-800 dark:bg-slate-900">
                            <th className="text-left p-3 font-medium">Bölge</th>
                            <th className="text-right p-3 font-medium">Paket</th>
                            <th className="text-right p-3 font-medium">Gün</th>
                            <th className="text-right p-3 font-medium">Ort/Gün</th>
                            <th className="text-right p-3 font-medium">Tutar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {packages.regions_breakdown.map((r, idx) => (
                            <tr key={idx} className="border-b hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800 dark:bg-slate-900">
                              <td className="p-3 font-medium">{r.region}</td>
                              <td className="p-3 text-right text-blue-600">{r.total_quantity.toLocaleString()}</td>
                              <td className="p-3 text-right">{r.total_days}</td>
                              <td className="p-3 text-right">{r.avg_daily}</td>
                              <td className="p-3 text-right text-green-600 font-medium">
                                {r.total_amount !== undefined ? `€ ${r.total_amount.toLocaleString('nl-NL', {minimumFractionDigits: 2})}` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Daily Breakdown Table - Admin sees amounts */}
              {packages.daily_breakdown?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-600" />
                      Günlük Detay
                    </CardTitle>
                    <CardDescription>
                      Son {Math.min(50, packages.daily_breakdown.length)} gün
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-white dark:bg-slate-800">
                          <tr className="border-b bg-slate-50 dark:bg-slate-800 dark:bg-slate-900">
                            <th className="text-left p-3 font-medium">Tarih</th>
                            <th className="text-left p-3 font-medium">Bölgeler</th>
                            <th className="text-right p-3 font-medium">Adet</th>
                            <th className="text-right p-3 font-medium">Tutar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {packages.daily_breakdown.slice(0, 50).map((d, idx) => (
                            <tr key={idx} className="border-b hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800 dark:bg-slate-900">
                              <td className="p-3">
                                {new Date(d.date).toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric', month: 'short' })}
                              </td>
                              <td className="p-3">
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(d.regions || {}).map(([region, qty]) => (
                                    <Badge key={region} variant="outline" className="text-xs">
                                      {region}: {qty}
                                    </Badge>
                                  ))}
                                </div>
                              </td>
                              <td className="p-3 text-right font-medium text-blue-600">
                                {d.total_quantity.toLocaleString()}
                              </td>
                              <td className="p-3 text-right font-medium text-green-600">
                                {d.total_amount !== undefined ? `€ ${d.total_amount.toLocaleString('nl-NL', {minimumFractionDigits: 2})}` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Paket verisi bulunamadı</p>
              <p className="text-sm mt-1">Bu çalışan için paket dağıtım verisi yüklendiğinde burada görüntülenecektir</p>
              <p className="text-xs mt-2 text-slate-300">
                Eşleştirme için lakap tanımlamayı unutmayın
              </p>
            </div>
          )}
        </TabsContent>
        )}

        {/* Loonbriefs (Payslips) Tab */}
        {canViewLoonbriefsTab && (
        <TabsContent value="loonbriefs" className="space-y-6">
          {loonbriefsLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : loonbriefs.length > 0 ? (
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wallet className="h-5 w-5 text-emerald-600" />
                      {t('employeeProfile.loonbriefsTitle') || 'Bodrolar'}
                    </CardTitle>
                    <CardDescription>
                      {targetUser?.full_name} {t('employeeProfile.loonbriefsDesc') || 'için yüklenmiş bordro dökümanları'}
                    </CardDescription>
                  </div>
                  {/* Year/Month Filters */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500 whitespace-nowrap">{t('employeeProfile.period') || 'Dönem'}:</span>
                    <Select value={lbFilterYear} onValueChange={(v) => { setLbFilterYear(v); setLbFilterMonth('all'); }}>
                      <SelectTrigger className="h-8 w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('employeeProfile.allYears') || 'Tüm Yıllar'}</SelectItem>
                        {[...new Set(loonbriefs.map(lb => {
                          if (lb.period) return lb.period.substring(0, 4);
                          return new Date(lb.created_at).getFullYear().toString();
                        }))].sort((a, b) => b - a).map(year => (
                          <SelectItem key={year} value={year}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={lbFilterMonth} onValueChange={setLbFilterMonth}>
                      <SelectTrigger className="h-8 w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('employeeProfile.selectMonth') || 'Ay Seç'}</SelectItem>
                        {Array.from({ length: 12 }, (_, i) => {
                          const m = String(i + 1).padStart(2, '0');
                          const label = new Date(2024, i, 1).toLocaleString(undefined, { month: 'long' });
                          return <SelectItem key={m} value={m}>{label}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {loonbriefs
                    .filter(lb => {
                      const period = lb.period || new Date(lb.created_at).toISOString().substring(0, 7);
                      if (lbFilterYear !== 'all' && !period.startsWith(lbFilterYear)) return false;
                      if (lbFilterMonth !== 'all' && period.substring(5, 7) !== lbFilterMonth) return false;
                      return true;
                    })
                    .map((lb) => (
                    <div 
                      key={lb.id} 
                      className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-100">
                            {lb.period ? (
                              <>
                                {new Date(lb.period + '-01').toLocaleString(undefined, { year: 'numeric', month: 'long' })}
                              </>
                            ) : lb.file_name}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {new Date(lb.created_at).toLocaleDateString()} {t('employeeProfile.uploadedOn') || 'tarihinde yüklendi'}
                            {lb.uploaded_by_name && <span className="ml-1">({lb.uploaded_by_name})</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreviewLoonbrief(lb)}
                          className="gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          {t('employeeProfile.viewBtn') || 'Görüntüle'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadLoonbrief(lb.id, lb.file_name)}
                          className="gap-1"
                        >
                          <Download className="h-4 w-4" />
                          {t('employeeProfile.downloadBtn') || 'İndir'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Wallet className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">{t('employeeProfile.noLoonbriefs') || 'Henüz bordro yüklenmemiş'}</p>
              <p className="text-sm mt-1">{t('employeeProfile.noLoonbriefsDesc') || 'Bordro Yükleme sayfasından bu çalışana bordro yükleyebilirsiniz'}</p>
            </div>
          )}
        </TabsContent>
        )}

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          {historyLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : history ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-green-600">{history.statistics.leave_counts.approved}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('employeeProfile.approved')}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-yellow-600">{history.statistics.leave_counts.pending}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('employeeProfile.pending')}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-blue-600">{history.statistics.total_leave_days}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('employeeProfile.leaveDays')}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-purple-600">{history.statistics.work_status_counts.WORKING || history.statistics.work_status_counts.working || 0}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('employeeProfile.workDays')}</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{t('employeeProfile.recentLeaves')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {history.leaves.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">{t('employeeProfile.noLeaves')}</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('common.type')}</TableHead>
                          <TableHead>{t('employeeProfile.approvedDates') || 'Onaylanan Tarihler'}</TableHead>
                          <TableHead>{t('common.status')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {history.leaves.slice(0, 15).map((leave, idx) => {
                          const statusMap = { APPROVED: t('leave.approved') || 'Onaylandı', PENDING: t('leave.pending') || 'Beklemede', REJECTED: t('leave.rejected') || 'Reddedildi' };
                          const localeMap = { tr: 'tr-TR', en: 'en-US', nl: 'nl-NL', fr: 'fr-FR' };
                          const dateLocale = localeMap[language] || 'tr-TR';
                          const typeName = (leave.leave_type_name_i18n && leave.leave_type_name_i18n[language]) || leave.leave_type_name || leave.leave_type_code || leave.leave_type || '-';
                          return (
                          <TableRow key={idx}>
                            <TableCell className="font-medium align-top">{typeName}</TableCell>
                            <TableCell>
                              {leave.selected_days && leave.selected_days.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {leave.selected_days.map((day, i) => (
                                    <span key={i} className="inline-block px-2 py-0.5 text-xs rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700">
                                      {new Date(day + 'T00:00:00').toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-sm text-slate-500">{formatDate(leave.start_date)} - {formatDate(leave.end_date)}</span>
                              )}
                            </TableCell>
                            <TableCell className="align-top">
                              <Badge className={
                                leave.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                                leave.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }>
                                {statusMap[leave.status] || leave.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          ) : null}
        </TabsContent>

        {/* Finance Tab - only visible if user has both financial flag and tab page-permission */}
        {canViewFinanceTab && (
        <TabsContent value="finance" className="space-y-6">
          {financeLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : filteredFinance ? (
            <>
              {/* Date Filter */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('common.filter') || 'Filtre'}:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant={financeFilter === 'all' ? 'default' : 'outline'} onClick={() => setFinanceFilter('all')}>
                        {t('common.all') || 'Tümü'}
                      </Button>
                      <Button size="sm" variant={financeFilter === 'lastMonth' ? 'default' : 'outline'} onClick={() => setFinanceFilter('lastMonth')}>
                        {t('common.lastMonth') || 'Geçen Ay'}
                      </Button>
                      <Button size="sm" variant={financeFilter === 'month' ? 'default' : 'outline'} onClick={() => setFinanceFilter('month')}>
                        {t('common.month') || 'Ay'}
                      </Button>
                      <Button size="sm" variant={financeFilter === 'year' ? 'default' : 'outline'} onClick={() => setFinanceFilter('year')}>
                        {t('common.year') || 'Yıl'}
                      </Button>
                      <Button size="sm" variant={financeFilter === 'range' ? 'default' : 'outline'} onClick={() => setFinanceFilter('range')}>
                        {t('common.dateRange') || 'Tarih Aralığı'}
                      </Button>
                    </div>
                    
                    {(financeFilter === 'month' || financeFilter === 'year') && (
                      <div className="flex items-center gap-2 ml-auto">
                        {financeFilter === 'month' && (
                          <Select value={String(financeMonth)} onValueChange={(v) => setFinanceMonth(Number(v))}>
                            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                                <SelectItem key={m} value={String(m)}>{new Date(2024, m-1).toLocaleString('tr', { month: 'long' })}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Select value={String(financeYear)} onValueChange={(v) => setFinanceYear(Number(v))}>
                          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[2024, 2025, 2026, 2027].map(y => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {financeFilter === 'range' && (
                      <div className="flex items-center gap-2 ml-auto">
                        <Input type="date" value={financeDateFrom} onChange={(e) => setFinanceDateFrom(e.target.value)} className="w-36" />
                        <span className="text-slate-400">-</span>
                        <Input type="date" value={financeDateTo} onChange={(e) => setFinanceDateTo(e.target.value)} className="w-36" />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Summary Cards - Compact */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-l-4 border-l-yellow-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{t('employeeProfile.advance') || 'Avans'}</p>
                        <p className="text-2xl font-bold text-yellow-600">{formatCurrency(filteredFinance.summary.total_avans || 0)}</p>
                      </div>
                      <Euro className="h-5 w-5 text-yellow-500" />
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className={`border-l-4 cursor-pointer hover:shadow-md ${(filteredFinance.summary.pot_balance || 0) >= 0 ? 'border-l-emerald-500' : 'border-l-rose-500'}`}
                  onClick={() => setPotDetailDialog(true)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-1">
                          <p className="text-sm text-slate-500 dark:text-slate-400">POT</p>
                          <Info className="h-3 w-3 text-slate-400" />
                        </div>
                        <p className={`text-2xl font-bold ${(filteredFinance.summary.pot_balance || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {(filteredFinance.summary.pot_balance || 0) >= 0 ? '+' : ''}{formatCurrency(filteredFinance.summary.pot_balance || 0)}
                        </p>
                      </div>
                      <Wallet className={`h-5 w-5 ${(filteredFinance.summary.pot_balance || 0) >= 0 ? 'text-emerald-500' : 'text-rose-500'}`} />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-cyan-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{t('employeeProfile.laadvergoeding') || 'Laadvergoeding'}</p>
                        <p className="text-2xl font-bold text-cyan-600">{formatCurrency(filteredFinance.summary.laadvergoeding || 0)}</p>
                      </div>
                      <Zap className="h-5 w-5 text-cyan-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Entries */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    {t('employeeProfile.transactionHistory')}
                    <Badge variant="secondary">{filteredFinance.entries.length} {t('common.records') || 'kayıt'}</Badge>
                  </CardTitle>
                  <Button onClick={() => setFinanceDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t('employeeProfile.addNew')}
                  </Button>
                </CardHeader>
                <CardContent>
                  {filteredFinance.entries.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">{t('employeeProfile.noTransactions')}</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('common.type')}</TableHead>
                          <TableHead>{t('common.amount')}</TableHead>
                          <TableHead>{t('common.date')}</TableHead>
                          <TableHead>{t('common.description')}</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredFinance.entries.map((entry, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className={`h-3 w-3 rounded-full ${FINANCE_TYPE_KEYS[entry.entry_type]?.color}`} />
                                {t(`employeeProfile.${FINANCE_TYPE_KEYS[entry.entry_type]?.key || 'otherType'}`)}
                              </div>
                            </TableCell>
                            <TableCell className={['odeme', 'prim', 'pot_plus', 'laadvergoeding'].includes(entry.entry_type) ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                              {['odeme', 'prim', 'pot_plus', 'laadvergoeding'].includes(entry.entry_type) ? '+' : '-'}{formatCurrency(entry.amount)}
                            </TableCell>
                            <TableCell>{formatDate(entry.date)}</TableCell>
                            <TableCell>{entry.description || '-'}</TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDeleteFinanceEntry(entry)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* POT Detail Dialog */}
              <Dialog open={potDetailDialog} onOpenChange={setPotDetailDialog}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Wallet className="h-5 w-5 text-emerald-600" />
                      POT {t('employeeProfile.details') || 'Detayları'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <Card className="bg-emerald-50 border-emerald-200">
                        <CardContent className="p-3 text-center">
                          <p className="text-xs text-emerald-700 font-medium">POT+</p>
                          <p className="text-lg font-bold text-emerald-600">+{formatCurrency(filteredFinance.summary.pot_plus || 0)}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-rose-50 border-rose-200">
                        <CardContent className="p-3 text-center">
                          <p className="text-xs text-rose-700 font-medium">POT-</p>
                          <p className="text-lg font-bold text-rose-600">-{formatCurrency(filteredFinance.summary.pot_minus || 0)}</p>
                        </CardContent>
                      </Card>
                      <Card className={`${(filteredFinance.summary.pot_balance || 0) >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                        <CardContent className="p-3 text-center">
                          <p className={`text-xs font-medium ${(filteredFinance.summary.pot_balance || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>Net</p>
                          <p className={`text-lg font-bold ${(filteredFinance.summary.pot_balance || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {(filteredFinance.summary.pot_balance || 0) >= 0 ? '+' : ''}{formatCurrency(filteredFinance.summary.pot_balance || 0)}
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                    <div className="border rounded-lg max-h-64 overflow-y-auto">
                      {filteredFinance.entries.filter(e => e.entry_type === 'pot_plus' || e.entry_type === 'pot_minus').length === 0 ? (
                        <p className="text-slate-400 text-center py-8 text-sm">{t('employeeProfile.noPotEntries') || 'POT kaydı bulunmuyor'}</p>
                      ) : (
                        <div className="divide-y">
                          {filteredFinance.entries.filter(e => e.entry_type === 'pot_plus' || e.entry_type === 'pot_minus').map((entry, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800 dark:bg-slate-900">
                              <div className="flex items-center gap-2">
                                <div className={`h-2 w-2 rounded-full ${entry.entry_type === 'pot_plus' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                <div>
                                  <p className="text-sm font-medium">{entry.entry_type === 'pot_plus' ? 'POT+ (Alacak)' : 'POT- (Borç)'}</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">{entry.description || '-'}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={`font-semibold ${entry.entry_type === 'pot_plus' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {entry.entry_type === 'pot_plus' ? '+' : '-'}{formatCurrency(entry.amount)}
                                </p>
                                <p className="text-xs text-slate-400">{formatDate(entry.date)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setPotDetailDialog(false)}>{t('common.close') || 'Kapat'}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          ) : null}
        </TabsContent>
        )}

        {/* Notes Tab */}
        {canViewNotesTab && (
        <TabsContent value="notes" className="space-y-6">
          {/* Add Note Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                {t('employeeProfile.addNote') || 'Not Ekle'}
              </CardTitle>
              <CardDescription>
                {t('employeeProfile.addNoteDesc') || 'Bu çalışan hakkında özel not ekleyin (sadece yöneticiler görebilir)'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder={t('employeeProfile.notePlaceholder') || 'Notunuzu buraya yazın...'}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={3}
                className="resize-none"
              />
              <div className="flex justify-end">
                <Button 
                  onClick={handleAddNote} 
                  disabled={addingNote || !newNote.trim()}
                  size="sm"
                >
                  {addingNote ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  {t('employeeProfile.addNote') || 'Not Ekle'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Notes List */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ScrollText className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                {t('employeeProfile.notesList') || 'Notlar'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {notesLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : notes.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t('employeeProfile.noNotes') || 'Henüz not eklenmemiş'}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notes.map((note) => (
                    <div key={note.id} className="border rounded-lg p-4 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800 dark:bg-slate-900 group">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                          <User className="h-4 w-4" />
                          <span className="font-medium">{note.created_by_name}</span>
                          <span>•</span>
                          <Calendar className="h-4 w-4" />
                          <span>{new Date(note.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteNoteClick(note.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{note.note}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Firm Transfer History Tab */}
        <TabsContent value="firm-history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                Firma Geçmişi
              </CardTitle>
              <CardDescription>
                Çalışanın firma atamaları ve transferleri — kim, ne zaman değiştirdi.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {firmHistoryLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : !firmHistory ? (
                <p className="text-sm text-slate-400 text-center py-10">Veri yok</p>
              ) : (
                <div className="relative pl-6 space-y-5 border-l-2 border-slate-200 dark:border-slate-700" data-testid="firm-history-timeline">
                  {targetUser?.employment_end_date && (
                    <div className="relative" data-testid="firm-history-exit">
                      <span className="absolute -left-[29px] top-1 flex items-center justify-center w-5 h-5 rounded-full bg-red-600 ring-4 ring-red-100 dark:ring-red-900/30">
                        <AlertTriangle className="h-3 w-3 text-white" />
                      </span>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-red-600 text-white border-0">İşten Ayrıldı</Badge>
                        <span className="font-semibold text-red-700 dark:text-red-300">
                          {new Date(targetUser.employment_end_date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">{firmHistory.current_firm?.firm_name || ''} firmasından ayrılış</p>
                    </div>
                  )}
                  {targetUser?.employment_start_date && (firmHistory.employment_periods || []).length > 0 && (
                    <div className="relative" data-testid="firm-history-current-period">
                      <span className="absolute -left-[29px] top-1 flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 ring-4 ring-emerald-100 dark:ring-emerald-900/30" />
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-emerald-600 text-white border-0">Yeniden İşe Alındı</Badge>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">
                          {new Date(targetUser.employment_start_date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">{firmHistory.current_firm?.firm_name || ''}</p>
                    </div>
                  )}
                  {(firmHistory.employment_periods || []).map((p, i, arr) => (
                    <div key={`${p.archived_at}-${i}`} className="relative" data-testid={`firm-history-period-${i}`}>
                      <span className="absolute -left-[29px] top-1 flex items-center justify-center w-5 h-5 rounded-full bg-slate-400 ring-4 ring-slate-100 dark:ring-slate-800" />
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Badge variant="outline" className="border-slate-300 text-slate-600">Dönem {arr.length - i}</Badge>
                        <span className="font-semibold text-slate-700 dark:text-slate-300">
                          {p.employment_start_date ? new Date(p.employment_start_date).toLocaleDateString('tr-TR') : '?'} – {new Date(p.employment_end_date).toLocaleDateString('tr-TR')}
                        </span>
                        <span className="text-xs font-medium text-red-600">İşten ayrıldı</span>
                      </div>
                      <p className="text-xs text-slate-500">{p.firm_name || '—'}{p.archived_by_name ? ` · yeniden işe alan: ${p.archived_by_name}` : ''}</p>
                    </div>
                  ))}
                  {/* Current (head) */}
                  <div className="relative">
                    <span className="absolute -left-[29px] top-1 flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 ring-4 ring-emerald-100 dark:ring-emerald-900/30">
                      <CheckCircle2 className="h-3 w-3 text-white" />
                    </span>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-emerald-100 text-emerald-700 border-0">Şu Anki Firma</Badge>
                      <span className="font-semibold text-slate-800 dark:text-slate-100">
                        {firmHistory.current_firm?.firm_name || <span className="text-slate-400 italic">Firma atanmamış</span>}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {firmHistory.history.length > 0
                        ? `Son değişiklik: ${new Date(firmHistory.history[0].changed_at).toLocaleString('tr-TR')}`
                        : 'Bu çalışan için firma değişikliği kaydedilmemiş.'}
                    </p>
                  </div>

                  {/* Transfer entries (newest first) */}
                  {firmHistory.history.map((h) => (
                    <div key={h.id} className="relative">
                      <span className="absolute -left-[29px] top-1 w-5 h-5 rounded-full bg-blue-500 ring-4 ring-blue-100 dark:ring-blue-900/30 flex items-center justify-center">
                        <Building2 className="h-3 w-3 text-white" />
                      </span>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge variant="outline" className="font-mono text-xs">
                          {h.from_firm_name || '—'}
                        </Badge>
                        <span className="text-slate-400">→</span>
                        <Badge className="bg-blue-100 text-blue-700 border-0 font-mono text-xs">
                          {h.to_firm_name || '—'}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500">
                        <span className="font-medium text-slate-600 dark:text-slate-300">{h.changed_by_name || 'Sistem'}</span>
                        {' · '}
                        {new Date(h.changed_at).toLocaleString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  ))}

                  {/* Creation baseline */}
                  <div className="relative">
                    <span className="absolute -left-[29px] top-1 w-5 h-5 rounded-full bg-slate-300 dark:bg-slate-600 ring-4 ring-slate-100 dark:ring-slate-800 flex items-center justify-center">
                      <User className="h-3 w-3 text-white" />
                    </span>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">Kullanıcı Oluşturuldu</Badge>
                    </div>
                    <p className="text-xs text-slate-500">
                      {firmHistory.created_at
                        ? new Date(firmHistory.created_at).toLocaleString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Add Finance Entry Dialog */}
      <Dialog open={financeDialog} onOpenChange={setFinanceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('employeeProfile.addFinanceEntry')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('common.type')} *</Label>
              <Select value={financeForm.entry_type} onValueChange={(v) => setFinanceForm({ ...financeForm, entry_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FINANCE_TYPE_KEYS).map(([key, { key: i18nKey }]) => (
                    <SelectItem key={key} value={key}>{t(`employeeProfile.${i18nKey}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('employeeProfile.amount')} (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={financeForm.amount}
                  onChange={(e) => setFinanceForm({ ...financeForm, amount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('employeeProfile.date')} *</Label>
                <Input
                  type="date"
                  value={financeForm.date}
                  onChange={(e) => setFinanceForm({ ...financeForm, date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('employeeProfile.description')}</Label>
              <Input
                value={financeForm.description}
                onChange={(e) => setFinanceForm({ ...financeForm, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinanceDialog(false)}>{t('employeeProfile.cancelBtn')}</Button>
            <Button onClick={handleAddFinanceEntry} disabled={savingFinance}>
              {savingFinance ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              {t('employeeProfile.addNew')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Finance Entry Confirmation */}
      <AlertDialog open={!!deleteFinanceEntry} onOpenChange={() => setDeleteFinanceEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('employeeProfile.deleteRecord')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('employeeProfile.deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('employeeProfile.cancelBtn')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFinanceEntry} className="bg-red-600 hover:bg-red-700">
              {t('employeeProfile.deleteBtn')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDocDialog} onOpenChange={setUploadDocDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              {t('employeeProfile.uploadDocument')}
            </DialogTitle>
            <DialogDescription>
              {targetUser?.full_name} {t('employeeProfile.forUpload') || 'için belge yükle'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* File Selection with Drag & Drop */}
            <div className="space-y-2">
              <Label>{t('employeeProfile.selectFile')} *</Label>
              <div 
                className={`
                  border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
                  ${isDragging 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                    : 'border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }
                `}
                onClick={() => docFileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {selectedDocFiles.length > 0 ? (
                  <div className="space-y-2">
                    {selectedDocFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileCheck className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm truncate">{file.name}</span>
                          <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={(e) => { e.stopPropagation(); removeDocFile(idx); }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500 mt-2">
                      {t('employeeProfile.clickToAddMore') || 'Daha fazla dosya eklemek için tıklayın veya sürükleyin'}
                    </p>
                  </div>
                ) : (
                  <>
                    <Upload className={`h-10 w-10 mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-slate-400'}`} />
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                      {t('employeeProfile.dragDropMultiple') || 'Dosyaları sürükleyip bırakın'}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {t('employeeProfile.orClickToSelect') || 'veya seçmek için tıklayın'}
                    </p>
                    <p className="text-xs text-slate-400 mt-2">{t('employeeProfile.fileTypes')}</p>
                  </>
                )}
              </div>
              <input
                ref={docFileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls"
                multiple
                onChange={handleDocFileSelect}
              />
            </div>

            {/* Custom File Name - Only show when single file is selected */}
            {selectedDocFiles.length === 1 && (
              <div className="space-y-2">
                <Label>{t('documents.fileName') || 'Dosya Adı'}</Label>
                <Input
                  placeholder={selectedDocFiles[0].name.split('.').slice(0, -1).join('.')}
                  value={customDocName}
                  onChange={(e) => setCustomDocName(e.target.value)}
                />
                <p className="text-xs text-slate-400">
                  {t('documents.originalName') || 'Orijinal'}: {selectedDocFiles[0].name}
                </p>
              </div>
            )}

            {/* Files count badge */}
            {selectedDocFiles.length > 1 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-300 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span><strong>{selectedDocFiles.length}</strong> {t('employeeProfile.filesSelected') || 'dosya seçildi'}</span>
                </p>
              </div>
            )}

            {/* Category */}
            <div className="space-y-2">
              <Label>{t('employeeProfile.category')}</Label>
              <Select value={docCategory} onValueChange={setDocCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERSONAL">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {t('employeeProfile.personal')}
                    </div>
                  </SelectItem>
                  <SelectItem value="COMPANY">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {t('employeeProfile.company')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Assigned User Info */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
                <User className="h-4 w-4" />
                <span><strong>{t('documents.assignedTo') || 'Atanan'}:</strong> {targetUser?.full_name}</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadDocDialog(false); setSelectedDocFiles([]); setCustomDocName(''); }}>{t('employeeProfile.cancelBtn')}</Button>
            <Button onClick={handleUploadDocument} disabled={uploadingDoc || selectedDocFiles.length === 0}>
              {uploadingDoc ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {selectedDocFiles.length > 1 
                ? `${t('employeeProfile.uploadBtn')} (${selectedDocFiles.length})`
                : t('employeeProfile.uploadBtn')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation Dialog */}
      <AlertDialog open={deleteDocDialog.open} onOpenChange={(open) => setDeleteDocDialog({ ...deleteDocDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              {t('employeeProfile.deleteDocumentTitle') || 'Belgeyi Sil'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>{t('employeeProfile.deleteDocumentConfirm') || 'Bu belgeyi silmek istediğinizden emin misiniz?'}</p>
              {deleteDocDialog.doc && (
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="font-medium">{deleteDocDialog.doc.file_name || deleteDocDialog.doc.original_name}</p>
                  <p className="text-sm text-slate-500">
                    {t('employeeProfile.category')}: {deleteDocDialog.doc.category === 'COMPANY' ? t('employeeProfile.company') : t('employeeProfile.personal')}
                  </p>
                </div>
              )}
              <p className="text-sm text-red-500">
                {t('employeeProfile.deleteDocumentWarning') || 'Bu işlem geri alınamaz.'}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingDoc}>
              {t('common.cancel') || 'İptal'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDocument}
              disabled={deletingDoc}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingDoc ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {t('common.delete') || 'Sil'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Document Dialog */}
      <Dialog open={editDocDialog.open} onOpenChange={(open) => setEditDocDialog({ ...editDocDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              {t('employeeProfile.editDocument') || 'Belgeyi Düzenle'}
            </DialogTitle>
            <DialogDescription>
              {editDocDialog.doc?.file_name || editDocDialog.doc?.original_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* File Name */}
            <div className="space-y-2">
              <Label htmlFor="edit-doc-file-name">Dosya Adı *</Label>
              <Input
                id="edit-doc-file-name"
                value={editDocFileName}
                onChange={(e) => setEditDocFileName(e.target.value)}
                placeholder="örn. arbeidsreglement_HYK.pdf"
                data-testid="edit-doc-file-name-input"
              />
              {(() => {
                // Live preview: smart-rename result + duplicate warning
                const origName = editDocDialog.doc?.original_name || editDocDialog.doc?.file_name || '';
                const typed = (editDocFileName || '').trim();
                const finalPreview = applySmartExtension(typed, origName);
                const willAutoExt = finalPreview && finalPreview !== typed;
                const duplicate = typed
                  ? documents.find(
                      d => d.id !== editDocDialog.doc?.id &&
                           (d.file_name || '').toLowerCase() === finalPreview.toLowerCase()
                    )
                  : null;
                return (
                  <div className="space-y-1">
                    {willAutoExt && (
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        Kaydedilirken şu şekilde saklanacak: <span className="font-mono font-medium">{finalPreview}</span>
                      </p>
                    )}
                    {duplicate && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Bu isim başka bir belgede zaten kullanılıyor. Kaydederken onay istenecek.
                      </p>
                    )}
                    {!willAutoExt && !duplicate && (
                      <p className="text-xs text-slate-500">
                        Dosya uzantısını (.pdf, .jpg vb.) boş bırakırsanız otomatik eklenir.
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Category Selection */}
            <div className="space-y-2">
              <Label>{t('employeeProfile.category')} *</Label>
              <Select value={editDocCategory} onValueChange={setEditDocCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERSONAL">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-500" />
                      {t('employeeProfile.personal') || 'Kişisel'}
                    </div>
                  </SelectItem>
                  <SelectItem value="COMPANY">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-purple-500" />
                      {t('employeeProfile.company') || 'Şirket Evrakı'}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {t('employeeProfile.categoryHint') || 'Belgenin kategorisini seçin'}
              </p>
            </div>

            {/* Current Info */}
            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-sm text-slate-500">
                {t('employeeProfile.currentCategory') || 'Mevcut Kategori'}: 
                <Badge variant="outline" className="ml-2">
                  {editDocDialog.doc?.category === 'COMPANY' ? t('employeeProfile.company') : t('employeeProfile.personal')}
                </Badge>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDocDialog({ open: false, doc: null })}>
              {t('common.cancel') || 'İptal'}
            </Button>
            <Button onClick={handleEditDocument} disabled={editingDoc}>
              {editingDoc ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              {t('common.save') || 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Photo Cropper */}
      <ImageCropper
        open={photoCropperOpen}
        onClose={() => setPhotoCropperOpen(false)}
        onComplete={handleUploadProfilePhoto}
        aspectRatio={1}
        cropShape="round"
        title={t('employeeProfile.uploadPhoto') || 'Profil Fotoğrafı Yükle'}
      />

      {/* Document Preview Modal (Images) */}
      <DocumentPreviewModal
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        document={previewDoc}
        title={previewDoc?.file_name || previewDoc?.original_name}
      />

      {/* Delete Note Confirmation Dialog */}
      <AlertDialog open={!!deleteNoteId} onOpenChange={() => setDeleteNoteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Notu Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu notu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDeleteNote}
              className="bg-red-600 hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* OCR Result Modal */}
      <Dialog open={ocrModalOpen} onOpenChange={setOcrModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-blue-500" />
              {t('employeeProfile.ocrResults') || 'OCR Tarama Sonuçları'}
            </DialogTitle>
            <DialogDescription>
              {t('employeeProfile.ocrResultsEditableDesc') || 'OCR taramasından bulunan tarihler aşağıda gösterilmektedir. Yanlışsa düzeltebilir veya manuel girebilirsiniz.'}
            </DialogDescription>
          </DialogHeader>
          
          {ocrModalData && (
            <div className="space-y-4 py-4">
              {/* Document Type */}
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <FileText className="h-4 w-4" />
                <span>Belge: </span>
                <Badge variant="outline">
                  {ocrModalData.docType === 'identity_card' ? 'Kimlik Kartı' :
                   ocrModalData.docType === 'driver_license' ? 'Ehliyet' :
                   ocrModalData.docType === 'bestuurdersattest' ? 'BESTUURDERSATTEST (Sürücü Belgesi)' :
                   'Pasaport'}
                </Badge>
              </div>
              
              {/* Editable Date Fields */}
              <div className="space-y-4 bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                {/* Expiry Date - Editable */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    📅 {t('employeeProfile.expiryDate') || 'Son Kullanma Tarihi'}
                    {ocrModalData.expiryDate && (
                      <Badge variant="secondary" className="text-xs">OCR</Badge>
                    )}
                  </Label>
                  <Input
                    type="date"
                    value={ocrModalData.expiryDate || ''}
                    onChange={(e) => setOcrModalData({ ...ocrModalData, expiryDate: e.target.value })}
                    className="bg-white dark:bg-slate-700"
                  />
                  {!ocrModalData.expiryDate && (
                    <p className="text-xs text-amber-600">⚠️ OCR son kullanma tarihi bulamadı. Manuel girin.</p>
                  )}
                  {/* Quick-pick any date detected in the document */}
                  {ocrModalData.allDates && ocrModalData.allDates.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-slate-500">Belgede bulunan tarihler — biri son kullanma olabilir:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Array.from(new Set(ocrModalData.allDates)).map((d) => (
                          <button
                            type="button"
                            key={d}
                            onClick={() => setOcrModalData({ ...ocrModalData, expiryDate: d })}
                            className={`px-2 py-1 rounded text-[11px] font-mono border transition-colors ${
                              ocrModalData.expiryDate === d
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white dark:bg-slate-700 border-slate-200 hover:border-blue-400'
                            }`}
                            data-testid={`ocr-date-suggestion-${d}`}
                          >
                            {new Date(d).toLocaleDateString('tr-TR')}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Birth Date - Editable (not relevant for Bestuurdersattest) */}
                {ocrModalData.docType !== 'bestuurdersattest' && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium">
                      🎂 {t('employeeProfile.birthDate') || 'Doğum Tarihi'}
                      {ocrModalData.birthDate && (
                        <Badge variant="secondary" className="text-xs">OCR</Badge>
                      )}
                    </Label>
                    <Input
                      type="date"
                      value={ocrModalData.birthDate || ''}
                      onChange={(e) => setOcrModalData({ ...ocrModalData, birthDate: e.target.value })}
                      className="bg-white dark:bg-slate-700"
                    />
                  </div>
                )}
                
                {/* Rijksregisternummer - Editable */}
                {ocrModalData.docType === 'identity_card' && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium" data-testid="ocr-rijksregisternr-label">
                      🆔 {t('employeeProfile.nationalId') || 'Rijksregisternummer'}
                      {ocrModalData.rijksregisternr && (
                        <Badge variant="secondary" className="text-xs">OCR</Badge>
                      )}
                    </Label>
                    <Input
                      type="text"
                      data-testid="ocr-rijksregisternr-input"
                      placeholder="YY.MM.DD-XXX.CC"
                      value={ocrModalData.rijksregisternr || ''}
                      onChange={(e) => {
                        const raw = stripRRN(e.target.value);
                        if (raw.length <= 11) {
                          setOcrModalData({ ...ocrModalData, rijksregisternr: raw.length === 11 ? formatRRN(raw) : e.target.value });
                        }
                      }}
                      className={(() => {
                        const v = validateRRN(ocrModalData.rijksregisternr, ocrModalData.birthDate);
                        if (!ocrModalData.rijksregisternr || stripRRN(ocrModalData.rijksregisternr).length < 11) return 'bg-white dark:bg-slate-700 font-mono';
                        if (!v.valid) return 'bg-white dark:bg-slate-700 font-mono border-red-500';
                        if (v.warning) return 'bg-white dark:bg-slate-700 font-mono border-amber-500';
                        return 'bg-white dark:bg-slate-700 font-mono border-green-500';
                      })()}
                    />
                    {(() => {
                      const digits = stripRRN(ocrModalData.rijksregisternr);
                      if (!digits || digits.length < 11) {
                        return !ocrModalData.rijksregisternr ? <p className="text-xs text-amber-600">OCR rijksregisternummer bulamadı. Manuel girin.</p> : null;
                      }
                      const result = validateRRN(ocrModalData.rijksregisternr, ocrModalData.birthDate);
                      const err = getRRNErrorMessage(result);
                      const warn = getRRNWarningMessage(result);
                      if (err) return <p className="text-xs text-red-600">{err}</p>;
                      if (warn) return <p className="text-xs text-amber-600">{warn}</p>;
                      return <p className="text-xs text-green-600">{result.gender === 'M' ? 'Erkek' : 'Kadın'} - {result.birthYear} doğumlu</p>;
                    })()}
                  </div>
                )}
              </div>
              
              {/* Other Found Dates - Clickable to Select */}
              {ocrModalData.allDates && ocrModalData.allDates.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500">
                    {t('employeeProfile.ocrAllDatesHint') || 'Bulunan diğer tarihler (tıklayarak seçin):'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {ocrModalData.allDates.map((date, idx) => (
                      <Button
                        key={idx}
                        variant={date === ocrModalData.expiryDate ? "default" : "outline"}
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => setOcrModalData({ ...ocrModalData, expiryDate: date })}
                        title="Son kullanma tarihi olarak seç"
                      >
                        {new Date(date).toLocaleDateString('tr-TR')}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* OCR Raw Text Preview */}
              {ocrModalData.text && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                    OCR Ham Metin (Gelişmiş)
                  </summary>
                  <pre className="mt-2 p-2 bg-slate-100 dark:bg-slate-900 rounded text-xs overflow-auto max-h-32 whitespace-pre-wrap">
                    {ocrModalData.text}
                  </pre>
                </details>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setOcrModalOpen(false); setOcrModalData(null); }}>
              {t('common.cancel') || 'İptal'}
            </Button>
            <Button 
              onClick={handleOcrConfirm} 
              className="bg-green-600 hover:bg-green-700" 
              data-testid="ocr-confirm-btn"
              disabled={!ocrModalData?.expiryDate && !ocrModalData?.birthDate && !ocrModalData?.rijksregisternr}
            >
              <Check className="h-4 w-4 mr-2" />
              {t('employeeProfile.applyOcrData') || 'Verileri Uygula'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
